/* ============================================================
   RUNEFORGE DISTRIBUTION — Theme JS
   Handles: splash, toasts, nav, collection filters,
   product carousel, qty steppers, all forms
   ============================================================ */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ============================================================
     INTRO SPLASH
     Mirrors prototype: auto-dismiss after 1800ms, click to skip,
     stores flag in sessionStorage.
     ============================================================ */
  function initSplash() {
    const splash = $('#rfd-splash');
    if (!splash) return;

    try {
      if (sessionStorage.getItem('rfd_intro')) {
        splash.classList.add('hidden');
        return;
      }
    } catch (e) {}

    let fading = false;
    const dismiss = () => {
      if (fading) return;
      fading = true;
      splash.classList.add('fading');
      setTimeout(() => {
        splash.classList.add('hidden');
        try { sessionStorage.setItem('rfd_intro', '1'); } catch (e) {}
      }, 520);
    };

    splash.addEventListener('click', dismiss);
    setTimeout(dismiss, 1800);
  }

  /* ============================================================
     TOAST NOTIFICATIONS
     Usage: window.rfdToast('Message here')
     ============================================================ */
  function initToasts() {
    window.rfdToast = function (msg) {
      const container = $('#rfd-toast-container');
      if (!container) return;

      const toast = document.createElement('div');
      toast.className = 'toast';
      toast.innerHTML = `
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        ${msg}
      `;
      container.appendChild(toast);

      setTimeout(() => {
        toast.classList.add('toast-out');
        setTimeout(() => toast.remove(), 280);
      }, 3200);
    };
  }

  /* ============================================================
     COLLECTION PAGE — Client-side filtering
     Reads data-* attributes from .rfd-product-item elements.
     Filters by: search, category, brand, stock, sort.
     Toggles between grid and list view.
     ============================================================ */
  function initCollection() {
    const collectionEl = $('#rfd-collection-page');
    if (!collectionEl) return;

    /* Brand trade names, published by the app to a shop metafield and stamped on
       the collection root. A brand can be sold under a name that is not the
       vendor field on its products; the pill shows that name while still
       filtering on the raw vendor. */
    const vendorNames = (() => {
      try {
        return JSON.parse(collectionEl.dataset.vendorNames || '{}') || {};
      } catch {
        return {};
      }
    })();
    const vendorSlug = (v) => String(v || '').toLowerCase().trim().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    function vendorLabel(v) {
      return vendorNames[vendorSlug(v)] || v;
    }


    const searchInput   = $('#rfd-filter-search');
    const catPills      = $$('.rfd-cat-pill');
    const stockPills    = $$('.rfd-stock-pill');
    const sortSelect    = $('#rfd-sort-select');
    const gridBtn       = $('#rfd-view-grid');
    const listBtn       = $('#rfd-view-list');
    const gridContainer = $('#rfd-product-grid');
    const listContainer = $('#rfd-product-list');
    const countEl       = $('#rfd-product-count');
    const clearBtn      = $('#rfd-clear-filters');
    const noResults     = $('#rfd-no-results');

    if (!gridContainer) return;

    let state = {
      search:   '',
      category: 'All',
      brand:    'All Brands',
      stock:    'All',
      sort:     'sku-asc',
      view:     'list',   /* collections open in list view */
      tagFilters: {},  /* { prefix: activeValue } */
      notBought: false, /* independent toggle — hide products already ordered */
    };

    /* Product ids the logged-in customer has ever ordered (from the app proxy).
       Empty until loaded; the pill stays hidden until then. */
    let purchasedIds = null;

    let allGridItems = [];
    let allListItems = [];

    function refreshItemArrays() {
      allGridItems = $$('.rfd-product-item--grid', gridContainer);
      allListItems = $$('.rfd-product-item--list', listContainer || document);
    }

    /* Featured products also appear at their natural sorted position (an
       "echo" clone) so someone scanning by SKU doesn't miss them at the top.
       Echoes are marked data-featured-echo: the pin tier ignores them, the
       sorter includes them, the visible count excludes them. Skipped: the
       metafield promo card (no SKU — no natural position) and the sample
       card. Cart forms inside clones keep working (all delegated). */
    function ensureFeaturedEchoes() {
      [gridContainer, listContainer].forEach((container) => {
        if (!container) return;
        const items = [...container.children];
        const have = new Set(
          items.filter((el) => el.dataset && el.dataset.featuredEcho === 'true').map((el) => el.dataset.echoOf),
        );
        items.forEach((el) => {
          if (!el.dataset || el.dataset.featured !== 'true') return;
          if (el.dataset.sample === 'true' || el.dataset.featuredEcho === 'true') return;
          const key = el.dataset.sku;
          if (!key || have.has(key)) return;
          const clone = el.cloneNode(true);
          clone.dataset.featuredEcho = 'true';
          clone.dataset.echoOf = key;
          delete clone.dataset.featured; // sorts as a normal item
          container.appendChild(clone);
          have.add(key);
        });
      });
    }

    ensureFeaturedEchoes();
    refreshItemArrays();

    function getItemData(item) {
      return {
        name:     (item.dataset.name     || '').toLowerCase(),
        brand:    (item.dataset.brand    || ''),
        sku:      (item.dataset.sku      || '').toLowerCase(),
        category: (item.dataset.category || ''),
        stock:    (item.dataset.stock    || ''),
        isNew:    item.dataset.isNew === 'true',
        newTs:    parseInt(item.dataset.newTs || '0', 10) || 0, /* NEW clock: new_from, else created_at */
        price:    parseFloat(item.dataset.price || '0'),
        title:    (item.dataset.title    || '').toLowerCase(),
        tags:     (item.dataset.tags     || '').toLowerCase().split('|'),
      };
    }

    /* `skip` leaves one facet out of the test: 'category', 'brand', 'stock' or
       'tag:<prefix>'. That is what refinePills needs — to know which values of a
       facet still lead somewhere, every OTHER filter has to apply but that one
       must not, or a facet would only ever offer the value already chosen. */
    function matchesFilters(item, skip) {
      const d = getItemData(item);
      // "Not yet bought" — independent of the stock pills, so it stacks.
      // Synthetic cards (sample/promo) have no product id and always pass.
      if (state.notBought && purchasedIds) {
        const pid = item.dataset.productId;
        if (pid && purchasedIds.has(pid)) return false;
      }
      if (skip !== 'category' && state.category !== 'All' && d.category !== state.category) return false;
      if (skip !== 'brand' && state.brand !== 'All Brands' && d.brand !== state.brand) return false;
      if (skip !== 'stock') {
        if (state.stock === 'In Stock'  && d.stock !== 'in-stock')  return false;
        if (state.stock === 'Pre-Order' && d.stock !== 'pre-order') return false;
        if (state.stock === 'New'       && !d.isNew)               return false;
      }
      if (state.search) {
        const q = state.search.toLowerCase();
        const brandText = `${d.brand} ${vendorLabel(d.brand)}`.toLowerCase();
        if (!d.name.includes(q) && !brandText.includes(q) && !d.sku.includes(q) && !d.title.includes(q)) return false;
      }
      for (const [prefix, value] of Object.entries(state.tagFilters)) {
        if (value === '__all__') continue;
        if (skip === 'tag:' + prefix) continue;
        const target = (prefix + value).toLowerCase();
        if (!d.tags.includes(target)) return false;
      }
      return true;
    }

    function sortItems(items) {
      return [...items].sort((a, b) => {
        const da = getItemData(a), db = getItemData(b);
        if (state.sort === 'az')         return da.title.localeCompare(db.title);
        if (state.sort === 'price-asc')  return da.price - db.price;
        if (state.sort === 'price-desc') return db.price - da.price;
        if (state.sort === 'sku-asc')    return da.sku.localeCompare(db.sku);
        if (state.sort === 'sku-desc')   return db.sku.localeCompare(da.sku);
        // New arrivals: NEW products first, then newest first WITHIN each group.
        // Comparing isNew alone tied every NEW product with every other, so once
        // a filter left only NEW products, every comparison was 0 and nothing moved.
        const byNew = (db.isNew ? 1 : 0) - (da.isNew ? 1 : 0);
        return byNew || (db.newTs - da.newTs);
      });
    }

    /* Arrange one set: the sample card (data-sample) pins first, then featured
       items (data-featured) in DOM order — both ignore the chosen sort but still
       respect filters/search; the rest sort normally below them. The sample is
       per-customer and only there when they qualify, so it leads; featured
       products are the same for everyone. Returns the visible count. */
    function arrangeSet(items) {
      const visible = items.filter(matchesFilters);
      const featured = visible.filter((i) => i.dataset.featured === 'true');
      const sampled = visible.filter((i) => i.dataset.sample === 'true');
      const normal = sortItems(visible.filter((i) => i.dataset.featured !== 'true' && i.dataset.sample !== 'true'));
      items.forEach((item) => { item.style.display = 'none'; });
      sampled.forEach((item, i) => { item.style.display = ''; item.style.order = -1000 + i; });
      featured.forEach((item, i) => { item.style.display = ''; item.style.order = -500 + i; });
      normal.forEach((item, i) => { item.style.display = ''; item.style.order = i; });
      // Echo clones are shown but don't count — they're duplicates of pinned items.
      const echoes = normal.filter((i) => i.dataset.featuredEcho === 'true').length;
      return featured.length + sampled.length + normal.length - echoes;
    }

    function render() {
      const gridCount = arrangeSet(allGridItems);

      /* Same for list items */
      if (listContainer) {
        const listCount = arrangeSet(allListItems);
        if (countEl) countEl.textContent = listCount + ' products';
      } else {
        if (countEl) countEl.textContent = gridCount + ' products';
      }

      if (noResults) noResults.style.display = gridCount === 0 ? 'block' : 'none';

      /* View toggle */
      if (gridContainer) gridContainer.style.display = state.view === 'grid' ? 'grid' : 'none';
      if (listContainer) listContainer.style.display = state.view === 'list' ? 'flex' : 'none';
      if (gridBtn) gridBtn.classList.toggle('active', state.view === 'grid');
      if (listBtn) listBtn.classList.toggle('active', state.view === 'list');

      // Every filter change narrows the other rows to what is still reachable.
      refinePills();
    }

    /* ──────────────────────────────────────────────────────────
       Prefetch all remaining pages via ?view=cards so filters
       work across the entire collection, not just first page.
       ────────────────────────────────────────────────────────── */
    function getCollectionHandle() {
      const m = window.location.pathname.match(/\/collections\/([^\/?#]+)/);
      return m ? m[1] : null;
    }

    async function loadAllProducts() {
      const handle = getCollectionHandle();
      if (!handle) return;

      // Read total pages from a Liquid-injected marker, or detect from existing pagination
      const pagEl = document.querySelector('[data-page-info]');
      let totalPages = pagEl ? parseInt(pagEl.dataset.totalPages || '1') : 1;
      if (!totalPages || totalPages <= 1) {
        // Fall back to scraping pagination links
        const last = document.querySelector('.rfd-pagination__btn--current');
        const links = document.querySelectorAll('.rfd-pagination__btn');
        let max = 1;
        links.forEach(l => {
          const n = parseInt(l.textContent.trim());
          if (!isNaN(n) && n > max) max = n;
        });
        totalPages = max;
      }
      if (totalPages <= 1) { refreshRestockBanner(); return; }

      // Show a tiny loading hint
      const loadingHint = document.createElement('div');
      loadingHint.className = 'rfd-collection__loading';
      loadingHint.textContent = 'Loading rest of catalogue…';
      if (countEl && countEl.parentElement) countEl.parentElement.appendChild(loadingHint);

      // Carry the page's sort_by into the prefetch — /collections/all redirects
      // itself to ?sort_by=title-ascending, and mixing server orderings across
      // pages would duplicate/drop products at the page boundaries.
      const sortBy = new URLSearchParams(window.location.search).get('sort_by');
      const url = `/collections/${handle}?view=cards${sortBy ? `&sort_by=${encodeURIComponent(sortBy)}` : ''}`;
      const pages = [];
      for (let p = 2; p <= totalPages; p++) pages.push(p);

      try {
        // Fetch all pages in parallel (capped to 4 concurrent to be polite)
        const chunks = [];
        for (let i = 0; i < pages.length; i += 4) {
          const batch = pages.slice(i, i + 4);
          const results = await Promise.all(batch.map(p => fetch(`${url}&page=${p}`, { credentials: 'same-origin' }).then(r => r.text())));
          chunks.push(...results);
        }

        // Parse HTML chunks into actual DOM nodes
        const parser = new DOMParser();
        const newGrid = [];
        const newList = [];
        chunks.forEach(html => {
          const doc = parser.parseFromString(html, 'text/html');
          doc.querySelectorAll('.rfd-product-item--grid').forEach(el => newGrid.push(el));
          doc.querySelectorAll('.rfd-product-item--list').forEach(el => newList.push(el));
        });

        // Append into containers
        if (gridContainer && newGrid.length) {
          newGrid.forEach(el => gridContainer.appendChild(el));
        }
        if (listContainer && newList.length) {
          newList.forEach(el => listContainer.appendChild(el));
        }

        // Refresh our cached arrays + re-render with current filters
        // (featured items on prefetched pages get their echo clone here too)
        ensureFeaturedEchoes();
        refreshItemArrays();

        // Hide the server-side pagination since the JS now controls the full set
        const pag = document.querySelector('.rfd-pagination');
        if (pag) pag.style.display = 'none';

        // Now that all products are loaded, rebuild vendor + tag pill lists
        // with values from the FULL catalogue (server-rendered only saw first 50)
        rebuildVendorPills();
        rebuildTagPills();
        pruneStockPills();

        render();
        refreshRestockBanner();
      } catch (err) {
        console.warn('rfd: could not preload all pages', err);
      } finally {
        loadingHint.remove();
      }
    }
    function handleize(s) {
      return String(s || '')
        .toLowerCase()
        .replace(/'/g, '')
        .replace(/&/g, '-and-')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    function pruneStockPills() {
      // Pre-Order / New are offered only when something the OTHER filters allow
      // actually qualifies — picking a brand with no pre-orders should take the
      // pill away, not leave it there to return an empty grid.
      const pool = allGridItems.filter(i => matchesFilters(i, 'stock'));
      const hasPreorder = pool.some(i => (i.dataset.stock || '') === 'pre-order');
      const hasNew = pool.some(i => (i.dataset.isNew || '') === 'true');
      const preBtn = document.querySelector('.rfd-stock-pill[data-value="Pre-Order"]');
      const newBtn = document.querySelector('.rfd-stock-pill[data-value="New"]');
      // Never take away the pill that is currently doing the filtering.
      if (preBtn) preBtn.style.display = hasPreorder || state.stock === 'Pre-Order' ? '' : 'none';
      if (newBtn) newBtn.style.display = hasNew || state.stock === 'New' ? '' : 'none';
    }

    /* Cross-facet pill refresh. The pill rows were built once from the whole
       catalogue, so choosing a brand left the other rows advertising values that
       brand has none of — clicking one returned nothing. Each row is now narrowed
       to the values still reachable given every other active filter. A row's own
       selection is excluded from its test, so brands stay switchable, and the
       active pill is never hidden. */
    function refinePills() {
      // Brands reachable under the other filters (the brand choice itself aside).
      const brands = new Set();
      allGridItems.forEach(i => {
        if (matchesFilters(i, 'brand')) {
          const v = (i.dataset.brand || '').trim();
          if (v) brands.add(v);
        }
      });
      document.querySelectorAll('.rfd-vendor-pill').forEach(p => {
        const v = p.dataset.value || '';
        p.hidden = !(v === 'All Brands' || v === state.brand || brands.has(v));
      });

      // Tag rows: one pass per prefix, each ignoring its own selection.
      const prefixes = new Set();
      document.querySelectorAll('.rfd-tag-pill[data-prefix]').forEach(p => {
        if (p.dataset.prefix) prefixes.add(p.dataset.prefix);
      });
      prefixes.forEach(prefix => {
        const pl = prefix.toLowerCase();
        const vals = new Set();
        allGridItems.forEach(i => {
          if (!matchesFilters(i, 'tag:' + prefix)) return;
          (i.dataset.tags || '').toLowerCase().split('|').forEach(t => {
            t = t.trim();
            if (t.startsWith(pl) && t.length > pl.length) vals.add(t.slice(pl.length));
          });
        });
        const active = (state.tagFilters[prefix] || '').toLowerCase();
        let shown = 0;
        document.querySelectorAll(`.rfd-tag-pill[data-prefix="${CSS.escape(prefix)}"]`).forEach(p => {
          const v = (p.dataset.value || '').toLowerCase();
          if (v === '__all__') return;
          const keep = vals.has(v) || (active && active === v);
          p.hidden = !keep;
          if (keep) shown++;
        });
        // A row down to a lone "All" says nothing — take the whole row away.
        const row = document.querySelector(`.rfd-filter-row--tag[data-tag-prefix="${CSS.escape(prefix)}"]`);
        if (row) row.style.display = shown ? '' : 'none';
      });

      pruneStockPills();
    }

    // Also prune immediately on first paint in case the server-rendered first
    // page already includes pre-order / new items
    pruneStockPills();

    function rebuildVendorPills() {
      const row = document.querySelector('.rfd-filter-row--vendor .rfd-filter-pills');
      if (!row) return;
      // Find the "All Brands" pill (always keep it as first child)
      let allPill = row.querySelector('button[data-value="All Brands"], .rfd-vendor-pill[data-value="All Brands"]');

      // Collect unique vendors from all loaded items
      const vendors = new Set();
      allGridItems.forEach(item => {
        const v = (item.dataset.brand || '').trim();
        if (v) vendors.add(v);
      });

      // Wipe existing brand pills (keep All Brands)
      Array.from(row.children).forEach(child => {
        if (child !== allPill) child.remove();
      });

      // Add a sorted button per vendor — uses client-side filtering.
      // The VALUE stays the raw vendor, because that is what the product cards
      // carry and what matchesFilters compares; only the label is the trade name.
      Array.from(vendors).sort((a, b) => a.localeCompare(b)).forEach(v => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'rfd-pill rfd-vendor-pill';
        b.dataset.value = v;
        const isActive = state.brand === v;
        if (isActive) b.classList.add('active');
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        b.textContent = vendorLabel(v);
        row.appendChild(b);
      });
    }

    function rebuildTagPills() {
      // Scheduled "featured" tag pill (blue + shimmer). The server renders it,
      // but this rebuild recreates the pills, so reapply from the root's data.
      // Match whitespace-insensitively: "type: magnets" (metafield) vs
      // "type:magnets" (prefix+value) vs "heavy metals" (value only).
      const normFeat = s => (s || '').toLowerCase().replace(/\s+/g, '');
      const featKey = normFeat(collectionEl.dataset.featuredTag);
      const featActive = collectionEl.dataset.featuredActive === 'true';
      // Group existing tag-pill containers by prefix
      const containers = {};
      document.querySelectorAll('.rfd-tag-pill[data-prefix]').forEach(p => {
        const pre = p.dataset.prefix;
        if (pre && !containers[pre]) containers[pre] = p.parentElement;
      });

      Object.entries(containers).forEach(([prefix, container]) => {
        const allBtn = container.querySelector('.rfd-tag-pill[data-value="__all__"]');
        const activeVal = state.tagFilters[prefix];

        // Collect unique values from all loaded items
        const values = new Set();
        const pl = prefix.toLowerCase();
        allGridItems.forEach(item => {
          const tags = (item.dataset.tags || '').toLowerCase().split('|');
          tags.forEach(t => {
            t = t.trim();
            if (t.startsWith(pl) && t.length > pl.length) {
              values.add(t.slice(pl.length));
            }
          });
        });

        // Wipe existing pills (keep All)
        Array.from(container.children).forEach(child => {
          if (child !== allBtn) child.remove();
        });

        // Add a sorted pill per value
        Array.from(values).sort((a, b) => a.localeCompare(b)).forEach(v => {
          if (!v) return;
          const b = document.createElement('button');
          b.className = 'rfd-pill rfd-tag-pill';
          b.dataset.prefix = prefix;
          b.dataset.value = v;
          const isActive = activeVal && activeVal.toLowerCase() === v;
          if (isActive) b.classList.add('active');
          if (featActive && featKey && (normFeat(prefix + v) === featKey || normFeat(v) === featKey)) {
            b.classList.add('is-featured-pill');
          }
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          b.textContent = v;
          container.appendChild(b);
        });

        // Hide the whole group if the full catalogue has no values for this
        // prefix (a lone "All" pill is useless); reveal it when it does.
        const row = container.closest('.rfd-filter-row--tag');
        if (row) row.style.display = values.size ? '' : 'none';
      });
    }

    /* ──────────────────────────────────────────────────────────
       Sample card — per-customer "try this brand" offer, driven by
       /apps/rfd-tools/samples/for-collection. Injected as a synthetic
       card (data-sample) pinned first, ahead of the featured slots. Single
       eligible sample = direct add; several = "Choose your samples"
       modal. Adds use property _rfd_sample (NOT _rfd_auto — the
       marketing cart-eval strips _rfd_auto lines it didn't decide on).
       ────────────────────────────────────────────────────────── */
    let sampleOffers = [];

    function sampleImgTag(s, cls) {
      return s.image ? `<img src="${s.image}" alt="" class="${cls}" loading="lazy">` : '';
    }

    function samplePriceText(s) {
      return s.price_cents > 0 ? rfdMoneyFormat(s.price_cents) : 'Free';
    }

    function removeSampleCards() {
      $$('.rfd-product-item--grid[data-sample], .rfd-product-item--list[data-sample]').forEach((el) => el.remove());
    }

    function renderSampleCards() {
      removeSampleCards();
      if (!sampleOffers.length) {
        refreshItemArrays();
        render();
        return;
      }
      const many = sampleOffers.length > 1;
      const first = sampleOffers[0];
      const title = many ? 'Free samples — choose yours' : first.title;
      const sub = many ? `${sampleOffers.length} samples available for you` : 'Free sample — one per dealer';
      const cta = many ? 'Choose your samples →' : `Add sample · ${samplePriceText(first)} →`;
      const nameAttr = sampleOffers.map((s) => s.title).join(' ').toLowerCase();

      const gridEl = document.createElement('div');
      gridEl.className = 'rfd-product-item--grid is-sample';
      gridEl.dataset.sample = 'true';
      gridEl.dataset.name = nameAttr;
      gridEl.dataset.title = title;
      gridEl.dataset.brand = ''; gridEl.dataset.sku = ''; gridEl.dataset.category = ''; gridEl.dataset.stock = '';
      gridEl.dataset.isNew = 'false'; gridEl.dataset.price = '0'; gridEl.dataset.tags = '';
      gridEl.innerHTML = `
        <span class="rfd-sample-ribbon">Sample</span>
        <div class="rfd-card rfd-card--sample">
          <div class="rfd-card__img">${sampleImgTag(first, 'rfd-card__img-photo rfd-card__img-primary')}</div>
          <div class="rfd-card__info">
            <div class="rfd-card__brand">${sub}</div>
            <div class="rfd-card__name">${title}</div>
          </div>
          <div class="rfd-card__actions">
            <button type="button" class="btn btn-primary btn-sm rfd-card__add" data-sample-cta style="flex:1;justify-content:center;">${cta}</button>
          </div>
        </div>`;

      const listEl = document.createElement('div');
      listEl.className = 'rfd-product-item--list is-sample';
      listEl.dataset.sample = 'true';
      listEl.dataset.name = nameAttr;
      listEl.dataset.title = title;
      listEl.dataset.brand = ''; listEl.dataset.sku = ''; listEl.dataset.category = ''; listEl.dataset.stock = '';
      listEl.dataset.isNew = 'false'; listEl.dataset.price = '0'; listEl.dataset.tags = '';
      listEl.innerHTML = `
        <div class="rfd-list-row rfd-list-row--promo rfd-list-row--sample">
          <span class="rfd-sample-ribbon rfd-sample-ribbon--sm">Sample</span>
          <div class="rfd-list-row__thumb" aria-hidden="true">${sampleImgTag(first, '')}</div>
          <div class="rfd-list-row--promo__info">
            <div class="rfd-list-row__brand">${sub}</div>
            <div class="rfd-list-row__name">${title}</div>
          </div>
          <button type="button" class="btn btn-primary btn-sm rfd-list-row--promo__cta" data-sample-cta style="font-size:11px;padding:7px 16px;flex-shrink:0;">${cta}</button>
        </div>`;

      const onCta = () => { if (sampleOffers.length > 1) openSampleModal(); else addSamples([sampleOffers[0]]); };
      gridEl.querySelector('[data-sample-cta]').addEventListener('click', onCta);
      listEl.querySelector('[data-sample-cta]').addEventListener('click', onCta);

      gridContainer.prepend(gridEl);
      if (listContainer) listContainer.prepend(listEl);
      refreshItemArrays();
      render();
    }

    async function addSamples(list) {
      let added = 0;
      for (const s of list) {
        try {
          const r = await fetch('/cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: Number(s.variant_id), quantity: 1, properties: { _rfd_sample: s.range_key } }),
          });
          if (r.ok) { added++; sampleOffers = sampleOffers.filter((x) => x !== s); }
        } catch (_) { /* keep the offer */ }
      }
      if (!added) return;
      try {
        const c = await rfdFetchCart();
        rfdUpdateCount(c);
        rfdRenderCart(c);
        rfdSyncCardsFromCart(c);
      } catch (_) { /* ignore */ }
      document.dispatchEvent(new CustomEvent('rfd:cart-updated'));
      if (window.rfdToast) window.rfdToast(added === 1 ? 'Sample added to your order' : `${added} samples added to your order`);
      renderSampleCards();
    }

    function openSampleModal() {
      const existing = $('#rfd-sample-modal');
      if (existing) existing.remove();
      const modal = document.createElement('div');
      modal.id = 'rfd-sample-modal';
      modal.className = 'rfd-modal is-open';
      modal.innerHTML = `
        <div class="rfd-modal__overlay" data-sample-close></div>
        <div class="rfd-modal__panel rfd-modal__panel--samples">
          <button type="button" class="rfd-modal__close" data-sample-close aria-label="Close">×</button>
          <div class="rfd-modal__header">
            <h3 class="rfd-modal__title">Choose your samples</h3>
            <p class="rfd-modal__sub">Free with your order — one of each per dealer.</p>
          </div>
          <div class="rfd-modal__body">
            ${sampleOffers.map((s, i) => `
              <label class="rfd-sample-modal__row">
                <input type="checkbox" data-sample-pick="${i}">
                <span class="rfd-sample-modal__thumb">${sampleImgTag(s, '')}</span>
                <span class="rfd-sample-modal__meta">
                  <span class="rfd-sample-modal__title">${s.title}</span>
                  <span class="rfd-sample-modal__price">${samplePriceText(s)}</span>
                </span>
              </label>`).join('')}
            <button type="button" class="btn btn-primary rfd-sample-modal__add" data-sample-confirm disabled>Add selected</button>
          </div>
        </div>`;
      document.body.appendChild(modal);

      const confirmBtn = modal.querySelector('[data-sample-confirm]');
      const sync = () => {
        const n = modal.querySelectorAll('[data-sample-pick]:checked').length;
        confirmBtn.disabled = n === 0;
        confirmBtn.textContent = n > 0 ? `Add ${n} sample${n === 1 ? '' : 's'}` : 'Add selected';
      };
      modal.addEventListener('change', sync);
      modal.querySelectorAll('[data-sample-close]').forEach((el) => el.addEventListener('click', () => modal.remove()));
      confirmBtn.addEventListener('click', async () => {
        const picked = [...modal.querySelectorAll('[data-sample-pick]:checked')]
          .map((cb) => sampleOffers[parseInt(cb.dataset.samplePick, 10)])
          .filter(Boolean);
        if (!picked.length) return;
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Adding…';
        await addSamples(picked);
        modal.remove();
      });
    }

    async function refreshSampleOffers() {
      if (!document.body.getAttribute('data-customer-id')) return; // logged-out → never offer
      const handle = getCollectionHandle();
      if (!handle) return;
      // Fetch the cart FIRST — its product ids feed the server's cart gate
      // (2+ distinct products of a range in the cart hides that range's sample).
      let cart = null;
      try { cart = await rfdFetchCart(); } catch (_) { /* no cart context */ }
      const cartIds = cart && cart.items ? [...new Set(cart.items.map((it) => String(it.product_id)))] : [];
      let offers = [];
      try {
        const qs = 'handle=' + encodeURIComponent(handle) + (cartIds.length ? '&cart=' + cartIds.join(',') : '');
        const r = await fetch('/apps/rfd-tools/samples/for-collection?' + qs, {
          credentials: 'same-origin', headers: { 'Accept': 'application/json' },
        });
        if (!r.ok) return;
        const data = await r.json();
        offers = (data && data.samples) || [];
      } catch (_) { return; }
      // Already sitting in the cart (added, not yet ordered) → don't re-offer.
      const inCart = new Set(cart && cart.items ? cart.items.map((it) => String(it.variant_id)) : []);
      sampleOffers = offers.filter((s) => s.variant_id && !inCart.has(String(s.variant_id)));
      renderSampleCards(); // empty offers → removes the card (live hide/reappear)
    }

    // Kick off after first paint
    if (window.requestIdleCallback) {
      requestIdleCallback(loadAllProducts);
    } else {
      setTimeout(loadAllProducts, 50);
    }
    refreshSampleOffers();

    /* ── "Not yet bought" filter ──────────────────────────────────────────
       Fetches the customer's ordered product ids once per session (cached in
       sessionStorage) and reveals the pill. Fails silently → pill stays
       hidden, collection behaves exactly as before. */
    /* ── FLAG: flip to true once Shopify grants `read_all_orders` ──────────
       Shopify's Order object only exposes the LAST 60 DAYS to an app holding
       plain `read_orders`. Without `read_all_orders` this filter reports
       anything bought earlier as "not yet bought" — worse than not shipping
       it — so the toggle stays hidden until the scope is approved.
       To re-enable: set this to true, add `read_all_orders` to the app's
       scopes in shopify.app.rfd-restock-pre-orders.toml, and deploy. */
    const NOT_BOUGHT_ENABLED = false;

    async function initNotBought() {
      if (!NOT_BOUGHT_ENABLED) return; // pending read_all_orders approval
      const pill = document.querySelector('[data-notbought]');
      if (!pill) return; // logged out — Liquid didn't render it
      const cacheKey = 'rfd:purchased:v1';
      let ids = null;
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.ts && (Date.now() - parsed.ts) < 600000) ids = parsed.ids;
        }
      } catch (_) {}
      if (!ids) {
        try {
          const r = await fetch('/apps/rfd-tools/purchased', {
            credentials: 'same-origin', headers: { Accept: 'application/json' },
          });
          if (!r.ok) return;
          const data = await r.json();
          ids = (data && data.productIds) || [];
          try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), ids })); } catch (_) {}
        } catch (_) { return; }
      }
      if (!ids.length) return; // never ordered anything → filter would be a no-op
      purchasedIds = new Set(ids.map(String));
      const bar = document.querySelector('[data-notbought-bar]');
      const stateEl = pill.querySelector('.rfd-notbought__state');
      if (bar) bar.style.display = '';
      pill.addEventListener('click', () => {
        state.notBought = !state.notBought;
        pill.classList.toggle('is-on', state.notBought);
        pill.setAttribute('aria-checked', state.notBought ? 'true' : 'false');
        if (stateEl) stateEl.textContent = state.notBought ? 'ON' : 'OFF';
        render();
      });
    }
    initNotBought();
    // Cart changes flip the gate both ways (2+ range products added → card
    // hides + cart-eval strips the sample; products removed → card returns).
    let sampleRefreshT = null;
    document.addEventListener('rfd:cart-updated', () => {
      clearTimeout(sampleRefreshT);
      sampleRefreshT = setTimeout(refreshSampleOffers, 600);
    });

    /* Pill helpers */
    // Picking a filter drops any search term. Search and filters compound, so a
    // leftover query silently emptied the grid the moment you touched a pill.
    function clearSearchOnFilter() {
      if (!state.search) return;
      state.search = '';
      if (searchInput) searchInput.value = '';
    }

    function bindPills(pills, stateKey) {
      pills.forEach(pill => {
        pill.addEventListener('click', () => {
          pills.forEach(p => p.classList.remove('active'));
          pill.classList.add('active');
          state[stateKey] = pill.dataset.value;
          clearSearchOnFilter();
          render();
        });
      });
    }

    bindPills(catPills,   'category');
    bindPills(stockPills, 'stock');

    /* Vendor pills — delegate so dynamically-added pills also work */
    document.addEventListener('click', (e) => {
      const pill = e.target.closest('.rfd-vendor-pill');
      if (!pill) return;
      e.preventDefault();
      document.querySelectorAll('.rfd-vendor-pill').forEach(p => {
        p.classList.remove('active');
        p.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add('active');
      pill.setAttribute('aria-pressed', 'true');
      state.brand = pill.dataset.value || 'All Brands';
      clearSearchOnFilter();
      render();
    });

    /* Tag filter pills — grouped by data-prefix */
    document.addEventListener('click', (e) => {
      const pill = e.target.closest('.rfd-tag-pill');
      if (!pill) return;
      const prefix = pill.dataset.prefix;
      const value  = pill.dataset.value;
      document.querySelectorAll(`.rfd-tag-pill[data-prefix="${CSS.escape(prefix)}"]`).forEach(p => {
        p.classList.remove('active');
        p.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add('active');
      pill.setAttribute('aria-pressed', 'true');
      state.tagFilters[prefix] = value;
      clearSearchOnFilter();
      render();
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => { state.search = searchInput.value; render(); });
      // Arrived via the header search icon (?focus=search) → drop the cursor in.
      if (/[?&]focus=search\b/.test(window.location.search)) {
        try { searchInput.focus({ preventScroll: false }); } catch (e) { searchInput.focus(); }
      }
    }
    if (sortSelect) {
      sortSelect.addEventListener('change', () => { state.sort = sortSelect.value; render(); });
    }
    if (gridBtn) gridBtn.addEventListener('click', () => {
      state.view = 'grid';
      state.sort = 'new';
      if (sortSelect) sortSelect.value = 'new';
      render();
    });
    if (listBtn) listBtn.addEventListener('click', () => {
      state.view = 'list';
      state.sort = 'sku-asc';
      if (sortSelect) sortSelect.value = 'sku-asc';
      render();
    });

    /* List is the default view, so seed the sort control to match it. */
    if (sortSelect) sortSelect.value = state.sort;

    /* ?view=grid opens in grid instead (?view=list is a no-op now). */
    if (/[?&]view=grid\b/.test(window.location.search) && gridBtn) {
      state.view = 'grid';
      state.sort = 'new';
      if (sortSelect) sortSelect.value = 'new';
    }

    /* List-row click navigation — navigate when click is NOT on an interactive
       element (button, link, stepper, SKU/barcode, etc.). Cmd/Ctrl/Shift click
       and middle-click open in a new tab. */
    function navigateListRow(e, row) {
      const href = row.dataset.href;
      if (!href) return;
      const newTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
      if (newTab) {
        window.open(href, '_blank', 'noopener');
      } else {
        window.location.assign(href);
      }
    }
    document.addEventListener('click', (e) => {
      const row = e.target.closest('.rfd-list-row[data-href]');
      if (!row) return;
      if (e.target.closest('button, a, input, select, textarea, [data-no-nav], .rfd-list-row__sku, .rfd-list-row__barcode')) return;
      navigateListRow(e, row);
    });
    // Middle-click fires auxclick (not click) in modern browsers
    document.addEventListener('auxclick', (e) => {
      if (e.button !== 1) return;
      const row = e.target.closest('.rfd-list-row[data-href]');
      if (!row) return;
      if (e.target.closest('button, a, input, select, textarea, [data-no-nav], .rfd-list-row__sku, .rfd-list-row__barcode')) return;
      e.preventDefault();
      navigateListRow(e, row);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        state = { search: '', category: 'All', brand: 'All Brands', stock: 'All', sort: 'new', view: state.view, tagFilters: {}, notBought: false };
        document.querySelectorAll('[data-notbought]').forEach(p => {
          p.classList.remove('is-on');
          p.setAttribute('aria-checked', 'false');
          const se = p.querySelector('.rfd-notbought__state');
          if (se) se.textContent = 'OFF';
        });
        if (searchInput) searchInput.value = '';
        catPills.forEach(p => p.classList.toggle('active', p.dataset.value === 'All'));
        stockPills.forEach(p => p.classList.toggle('active', p.dataset.value === 'All'));
        document.querySelectorAll('.rfd-tag-pill').forEach(p => {
          const isAll = p.dataset.value === '__all__';
          p.classList.toggle('active', isAll);
          p.setAttribute('aria-pressed', isAll ? 'true' : 'false');
        });
        if (sortSelect) sortSelect.value = 'new';
        render();
      });
    }

    // Hide the "Stay in the loop on restocks" banner when every product in the
    // (fully loaded) collection is in stock — nothing to notify about. Called
    // only once the full catalogue is known (after prefetch, or single-page).
    function refreshRestockBanner() {
      const banner = document.querySelector('.rfd-brand-restock-banner');
      if (!banner) return;
      const anyNotInStock = allGridItems.some(i => (i.dataset.stock || '') !== 'in-stock');
      banner.style.display = anyNotInStock ? '' : 'none';
    }

    render();
  }

  /* ============================================================
     QTY STEPPERS — product cards and list rows
     ============================================================ */
  function initSteppers() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-stepper-action]');
      if (!btn) return;
      e.stopPropagation();

      const action = btn.dataset.stepperAction;
      const stepper = btn.closest('[data-stepper]');
      if (!stepper) return;

      const valEl     = stepper.querySelector('[data-stepper-val]');
      const moq       = parseInt(stepper.dataset.moq       || '1');
      const increment = parseInt(stepper.dataset.increment || '1');
      const isInput   = valEl && valEl.tagName === 'INPUT';
      let   val       = parseInt(isInput ? valEl.value : (valEl?.textContent || moq)) || moq;

      if (action === 'dec') val = Math.max(moq, val - increment);
      if (action === 'inc') val = val + increment;

      if (valEl) {
        if (isInput) valEl.value = val;
        else         valEl.textContent = val;
      }
      // Sync hidden form quantity (sibling form on same card / row)
      const card = stepper.closest('.rfd-card, .rfd-list-row, [data-product-card]');
      if (card) {
        const qtyInput = card.querySelector('.rfd-atc-qty');
        if (qtyInput) qtyInput.value = val;
      }
    });

    /* Manual qty input: sync to hidden cart form + snap to increment on blur */
    document.addEventListener('input', (e) => {
      const inp = e.target;
      if (!(inp instanceof HTMLInputElement)) return;
      if (!inp.matches('[data-stepper-val]')) return;
      const stepper = inp.closest('[data-stepper]');
      if (!stepper) return;
      const card = stepper.closest('.rfd-card, .rfd-list-row, [data-product-card]');
      const qtyInput = card && card.querySelector('.rfd-atc-qty');
      if (qtyInput) qtyInput.value = inp.value;
    });
    document.addEventListener('change', (e) => {
      const inp = e.target;
      if (!(inp instanceof HTMLInputElement)) return;
      if (!inp.matches('[data-stepper-val]')) return;
      const stepper = inp.closest('[data-stepper]');
      if (!stepper) return;
      const moq       = parseInt(stepper.dataset.moq       || '1');
      const increment = parseInt(stepper.dataset.increment || '1') || 1;
      let val = parseInt(inp.value);
      if (isNaN(val) || val < moq) val = moq;
      // Snap to nearest valid multiple (>= moq)
      if (increment > 1) {
        const stepsFromBase = Math.round((val - moq) / increment);
        val = moq + Math.max(0, stepsFromBase) * increment;
      }
      inp.value = val;
      const card = stepper.closest('.rfd-card, .rfd-list-row, [data-product-card]');
      const qtyInput = card && card.querySelector('.rfd-atc-qty');
      if (qtyInput) qtyInput.value = val;
    });

    /* Product page qty input */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-product-qty-action]');
      if (!btn) return;
      const action = btn.dataset.productQtyAction;
      const input  = $('#rfd-product-qty');
      if (!input) return;
      const moq       = parseInt(input.dataset.moq       || '1');
      const increment = parseInt(input.dataset.increment || '1');
      let   val       = parseInt(input.value)            || moq;
      if (action === 'dec') val = Math.max(moq, val - increment);
      if (action === 'inc') val = val + increment;
      input.value = val;
    });
  }

  /* ============================================================
     CART — drawer, line items, increment-snapped quantity changes
     ============================================================ */
  function rfdMoneyFormat(cents) {
    const v = (cents / 100).toFixed(2);
    return '$' + v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* What the prepaid tariffs come to as a share of the cart. The per-origin
     rates differ, so a single blended figure is the only honest headline — hence
     "avg." rather than a bare percentage. */
  function rfdTariffAvgPct(cart, q) {
    const base = (cart && cart.total_price) || 0;
    const tariff = (q && q.totalCents) || 0;
    if (base <= 0 || tariff <= 0) return '';
    const pct = (tariff / base) * 100;
    return 'avg. +' + (pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)) + '%';
  }

  function rfdGetIncrement(line) {
    const props = line.properties || {};
    const fromProp = parseInt(props._increment || '0');
    if (fromProp > 0) return fromProp;
    return 1;
  }
  function rfdGetMinQty(line) {
    const props = line.properties || {};
    const fromProp = parseInt(props._min_qty || '0');
    if (fromProp > 0) return fromProp;
    return rfdGetIncrement(line);
  }

  let rfdCartCache = null;

  async function rfdFetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    rfdCartCache = await res.json();
    return rfdCartCache;
  }

  function rfdUpdateCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.setAttribute('data-cart-count', cart.item_count);
    });
    const dc = document.querySelector('[data-drawer-count]');
    if (dc) dc.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
  }

  function rfdRenderCart(cart) {
    const body = document.querySelector('[data-cart-body]');
    const footer = document.querySelector('[data-cart-footer]');
    if (!body) return;

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = '<div class="rfd-drawer__empty">Your cart is empty.</div>';
      if (footer) footer.style.display = 'none';
      return;
    }

    body.innerHTML = cart.items.map((item, i) => {
      const inc = rfdGetIncrement(item);
      const minQty = rfdGetMinQty(item);
      const img = item.image ? `<img src="${item.image.replace('.jpg', '_120x.jpg')}" alt="">` : '';
      const incrementNote = inc > 1 ? `<div class="rfd-cart-line__increment-note">Increments of ${inc}</div>` : '';
      return `
        <div class="rfd-cart-line" data-line-key="${item.key}" data-increment="${inc}" data-min-qty="${minQty}">
          <a href="${item.url}" class="rfd-cart-line__img">${img}</a>
          <div>
            <div class="rfd-cart-line__brand">${item.vendor || ''}</div>
            <a href="${item.url}" class="rfd-cart-line__name">${item.product_title}</a>
            <div class="rfd-cart-line__sku">${item.sku || ''}</div>
            <div class="rfd-cart-line__qty">
              <button type="button" data-line-dec aria-label="Decrease">−</button>
              <input type="number" data-line-qty value="${item.quantity}" min="${minQty}" step="${inc}" inputmode="numeric" aria-label="Quantity">
              <button type="button" data-line-inc aria-label="Increase">+</button>
            </div>
            ${incrementNote}
          </div>
          <div class="rfd-cart-line__right">
            <div class="rfd-cart-line__price">${rfdMoneyFormat(item.final_line_price)}</div>
            <div class="rfd-cart-line__tariff" data-tariff-line="${item.key}"></div>
            <button type="button" class="rfd-cart-line__remove" data-line-remove>Remove</button>
          </div>
        </div>
      `;
    }).join('');

    const sub = document.querySelector('[data-cart-subtotal]');
    if (sub) sub.textContent = rfdMoneyFormat(cart.total_price);
    if (footer) footer.style.display = 'flex';
    rfdCartExtras(cart);
  }

  /* ----------------------------------------------------------
     Sync every Add-to-Cart form on the page with cart state.
     If the variant is in the cart, switch the form to "adjust"
     mode: button text → ADJUST, stepper qty → cart qty, submit
     uses /cart/change instead of /cart/add.
     ---------------------------------------------------------- */
  function rfdSyncCardsFromCart(cart) {
    if (!cart || !cart.items) return;
    const byVariant = {};
    cart.items.forEach(item => {
      byVariant[String(item.variant_id)] = item;
    });
    document.querySelectorAll('form.rfd-atc-form[data-variant-id]').forEach(form => {
      const vid = String(form.dataset.variantId || '');
      const inCart = byVariant[vid];
      const btn = form.querySelector('button[type="submit"]');
      // Find the stepper input on the same card/row (or product page)
      const card = form.closest('.rfd-card, .rfd-list-row, .rfd-product__inner, [data-product-card]');
      const stepperInput = card && (card.querySelector('[data-stepper-val]') || card.querySelector('#rfd-product-qty'));
      const qtyInput = form.querySelector('.rfd-atc-qty');

      if (inCart) {
        form.dataset.mode = 'adjust';
        form.dataset.lineKey = inCart.key;
        if (btn && !btn.disabled) {
          btn.dataset.origLabel = btn.dataset.origLabel || btn.textContent.trim();
          btn.textContent = 'Adjust';
          btn.classList.add('is-adjust');
        }
        if (stepperInput) {
          if (stepperInput.tagName === 'INPUT') stepperInput.value = inCart.quantity;
          else stepperInput.textContent = inCart.quantity;
        }
        if (qtyInput) qtyInput.value = inCart.quantity;
      } else {
        // Variant not in cart — restore original Add label
        form.dataset.mode = 'add';
        delete form.dataset.lineKey;
        if (btn && btn.dataset.origLabel) {
          btn.textContent = btn.dataset.origLabel;
          btn.classList.remove('is-adjust');
        }
      }
    });
  }

  async function rfdChangeLine(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    if (!res.ok) throw new Error('cart change failed');
    rfdCartCache = await res.json();
    rfdUpdateCount(rfdCartCache);
    rfdRenderCart(rfdCartCache);
    rfdSyncCardsFromCart(rfdCartCache);
    document.dispatchEvent(new CustomEvent('rfd:cart-updated'));
    return rfdCartCache;
  }

  async function rfdClearCart() {
    const res = await fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error('cart clear failed');
    rfdCartCache = await res.json();
    rfdUpdateCount(rfdCartCache);
    rfdRenderCart(rfdCartCache);
    rfdSyncCardsFromCart(rfdCartCache);
    document.dispatchEvent(new CustomEvent('rfd:cart-updated'));
    return rfdCartCache;
  }

  function rfdSnapQty(qty, increment, currentQty) {
    if (increment <= 1) return Math.max(0, qty);
    if (qty <= 0) return 0;
    // Round to nearest increment, but bias up if user clicked +, down if user clicked -
    if (qty > currentQty) return Math.ceil(qty / increment) * increment;
    if (qty < currentQty) return Math.floor(qty / increment) * increment;
    return Math.round(qty / increment) * increment;
  }

  /* ----------------------------------------------------------
     US tariffs (DDP) + discount codes — cart drawer and cart page.
     window.RFD_TARIFFS.us is set by the layout for logged-in customers
     shopping in the US market while tariffs are on (RFD HUB → Tariffs).
     - per-line surcharge + recap from /apps/rfd-tools/tariffs/quote
     - checkout → the app builds a draft order (same items + discount
       codes + the tariff line) and we send the customer to its Shopify
       checkout; the draft id rides on the cart as _rfd_tariff_ref
     - back on the store after paying: remove the paid items from THIS
       cart, once (the regular checkout does that itself; drafts don't)
     ---------------------------------------------------------- */
  // Shopify collects duties at its own checkout; this only gates the ESTIMATE we
  // show beforehand (product prices and the cart recap). Nothing here touches
  // checkout any more.
  const RFD_US_TARIFFS = !!(window.RFD_TARIFFS && window.RFD_TARIFFS.us);
  let rfdTariffSig = '';
  let rfdTariffQuote = null;
  let rfdTariffTimer = null;

  function rfdEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  // One decimal: the stored rate can carry three, and false precision on a
  // figure Shopify settles exactly at checkout is worse than none.
  function rfdPct(p) { return String(Math.round((Number(p) || 0) * 10) / 10); }

  /* ----------------------------------------------------------
     Shipping progress. Two milestones on one track: the order
     minimum, then the point where shipping gets cheaper. The
     bar runs 0 → threshold, with the minimum marked along it,
     so the thing you are working towards is always the far end.
     ---------------------------------------------------------- */
  /** Milestones are round numbers; the cents only matter on the amount to go. */
  function rfdMoneyShort(cents) {
    return cents % 100 === 0 ? rfdMoneyFormat(cents).replace(/\.00$/, '') : rfdMoneyFormat(cents);
  }

  /* Below the order minimum our carrier service returns NO rates, and Shopify's
     wording for that is "we don't ship to your address" — which is not the
     reason and sends people away thinking we don't deliver to them. Checkout is
     held here instead, with the real reason next to the button. A direct
     /checkout URL still bypasses this, which is what the server-side validation
     covers; this is the honest message, not the enforcement. */
  let rfdCheckoutBlocked = false;

  function rfdSetCheckoutBlocked(blocked, reason) {
    rfdCheckoutBlocked = blocked;
    document.querySelectorAll('[data-checkout]').forEach((el) => {
      el.classList.toggle('is-disabled', blocked);
      el.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      if (el.tagName === 'BUTTON') el.disabled = blocked;
      if (blocked) el.setAttribute('title', reason);
      else el.removeAttribute('title');
    });
    document.querySelectorAll('[data-checkout-note]').forEach((el) => {
      el.textContent = blocked ? reason : '';
      el.hidden = !blocked;
    });
  }

  // The drawer's control is an <a>, which ignores `disabled` — catch it in the
  // capture phase so nothing else can act on the click first.
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target && e.target.closest && e.target.closest('[data-checkout]');
      if (el && rfdCheckoutBlocked) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );

  function rfdShipProgress(cart) {
    const total = Math.max(0, Number(cart && cart.total_price) || 0);
    const bars = document.querySelectorAll('[data-ship]');
    // No bar means no policy to judge against (logged out, or none published) —
    // never hold checkout on an absence.
    if (!bars.length) rfdSetCheckoutBlocked(false, '');
    let shortBy = 0;
    let minimum = 0;
    // A multi-store cart (set by the order import) owes a slice per store, on
    // the combined total: the higher of the normal minimum and per-store × stores.
    const msStores = Number(cart && cart.attributes && cart.attributes._rfd_multistore_stores) || 0;
    bars.forEach((el) => {
      const baseMin = Number(el.dataset.min) || 0;
      const msMin = msStores ? (Number(el.dataset.msPerStore) || 0) * msStores : 0;
      const min = Math.max(baseMin, msMin);
      const msRule = msMin > baseMin;
      const threshold = el.dataset.threshold ? Number(el.dataset.threshold) : 0;
      const aboveFree = String(el.dataset.aboveFree) === 'true';
      const abovePct = Number(el.dataset.abovePct) || 0;
      const top = threshold > min ? threshold : min;
      if (!top) { el.hidden = true; return; }
      el.hidden = false;

      const fill = el.querySelector('[data-ship-fill]');
      const tick = el.querySelector('[data-ship-tick]');
      const msg = el.querySelector('[data-ship-msg]');
      const a = el.querySelector('[data-ship-a]');
      const b = el.querySelector('[data-ship-b]');

      if (fill) fill.style.width = Math.min(100, (total / top) * 100) + '%';
      // The minimum only needs marking when it sits short of the far end.
      if (tick) {
        const show = threshold > min && min > 0;
        tick.hidden = !show;
        if (show) tick.style.left = (min / top) * 100 + '%';
      }

      const belowMin = total < min;
      if (belowMin && min - total > shortBy) {
        shortBy = min - total;
        minimum = min;
      }
      const complete = threshold > 0 && total >= threshold;
      el.classList.toggle('is-below-min', belowMin);
      el.classList.toggle('is-complete', complete);

      if (msg) {
        const flat = String(el.dataset.lowerType || 'flat') === 'flat';
        const flatAmount = Number(el.dataset.lowerFlat) || 0;
        if (belowMin) {
          msg.textContent = 'Add ' + rfdMoneyFormat(min - total) + ' to reach the ' + rfdMoneyShort(min) + ' minimum'
            + (msRule ? ' for ' + msStores + ' stores' : '');
        } else if (threshold > 0 && total < threshold) {
          msg.textContent = aboveFree
            ? 'Add ' + rfdMoneyFormat(threshold - total) + ' for free shipping'
            : 'Add ' + rfdMoneyFormat(threshold - total) + ' for ' + (abovePct ? abovePct + '% flat shipping' : 'flat shipping');
        } else if (aboveFree) {
          msg.textContent = '✓ Free shipping unlocked';
        } else if (threshold > 0) {
          // Past the threshold the rate is the percentage, not the flat fee.
          msg.textContent = abovePct ? '✓ ' + abovePct + '% flat shipping applied' : '✓ Flat shipping applied';
        } else if (flat) {
          // No threshold at all: one flat fee the whole way up. Calling that
          // "reduced" was meaningless — there is nothing it is reduced from.
          msg.textContent = flatAmount ? '✓ ' + rfdMoneyFormat(flatAmount) + ' flat shipping' : '✓ Flat shipping';
        } else {
          msg.textContent = '✓ Flat shipping applied';
        }
      }
      if (a) a.textContent = min ? rfdMoneyShort(min) + ' min' : '';
      if (b) {
        b.textContent = threshold
          ? rfdMoneyShort(threshold) + (aboveFree ? ' free' : abovePct ? ' · ' + abovePct + '%' : '')
          : '';
      }
    });

    if (bars.length) {
      rfdSetCheckoutBlocked(
        shortBy > 0,
        shortBy > 0
          ? 'Minimum order is ' + rfdMoneyShort(minimum) + ' — add ' + rfdMoneyFormat(shortBy) + ' to check out'
          : '',
      );
    }
  }

  function rfdCartExtras(cart) {
    rfdRenderDiscounts(cart);
    // Before the tariff gate below: the bar is for every market, not just the US.
    rfdShipProgress(cart);
    if (!RFD_US_TARIFFS) return;
    clearTimeout(rfdTariffTimer);
    rfdTariffTimer = setTimeout(() => rfdQuoteTariffs(cart), 120);
  }

  function rfdRenderDiscounts(cart) {
    const codes = (cart && cart.discount_codes) || [];
    const saved = Math.max(0, Number(cart && cart.total_discount) || 0);
    document.querySelectorAll('[data-discount]').forEach((box) => {
      const list = box.querySelector('[data-discount-codes]');
      if (!list) return;
      list.innerHTML = codes.map((d) => `
        <span class="rfd-discount__chip${d.applicable ? '' : ' is-na'}">
          ${rfdEsc(d.code)}${d.applicable ? '' : ' · not applicable'}
          <button type="button" data-discount-remove="${rfdEsc(d.code)}" aria-label="Remove code ${rfdEsc(d.code)}">×</button>
        </span>`).join('') + (saved > 0 ? `<span class="rfd-discount__saved">Discounts −${rfdMoneyFormat(saved)}</span>` : '');
    });
  }

  function rfdDiscountMsg(box, text) {
    const m = box && box.querySelector('[data-discount-msg]');
    if (!m) return;
    m.textContent = text || '';
    m.hidden = !text;
  }

  async function rfdSetDiscounts(codes) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!res.ok) throw new Error('discount update failed');
    rfdCartCache = await res.json();
    return rfdCartCache;
  }

  async function rfdAfterDiscountChange(cart) {
    if (document.querySelector('.rfd-cart-page__row')) { window.location.reload(); return; }
    rfdUpdateCount(cart);
    rfdRenderCart(cart);
    document.dispatchEvent(new CustomEvent('rfd:cart-updated'));
  }

  async function rfdApplyDiscount(box) {
    const inp = box.querySelector('[data-discount-input]');
    const btn = box.querySelector('[data-discount-apply]');
    const code = (inp && inp.value || '').trim();
    if (!code) return;
    rfdDiscountMsg(box, '');
    if (btn) btn.disabled = true;
    try {
      const cart = rfdCartCache || (await rfdFetchCart());
      const cur = (cart.discount_codes || []).map((d) => d.code);
      if (cur.some((c) => c.toLowerCase() === code.toLowerCase())) { inp.value = ''; return; }
      const next = await rfdSetDiscounts(cur.concat(code));
      const got = (next.discount_codes || []).find((d) => String(d.code).toLowerCase() === code.toLowerCase());
      inp.value = '';
      if (!got) rfdDiscountMsg(box, `“${code}” isn't a valid code.`);
      else if (!got.applicable) rfdDiscountMsg(box, `“${code}” doesn't apply to this cart.`);
      await rfdAfterDiscountChange(next);
    } catch (_) {
      rfdDiscountMsg(box, 'Could not apply the code. Try again.');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  async function rfdRemoveDiscount(code) {
    try {
      const cart = rfdCartCache || (await rfdFetchCart());
      const cur = (cart.discount_codes || []).map((d) => d.code).filter((c) => c !== code);
      await rfdAfterDiscountChange(await rfdSetDiscounts(cur));
    } catch (_) {
      window.rfdToast('Could not remove the code.');
    }
  }

  function rfdTariffCartSig(cart) {
    return [cart.token, cart.currency, cart.total_price, (cart.items || []).map((i) => `${i.key}:${i.quantity}:${i.final_line_price}`).join(',')].join('|');
  }

  async function rfdQuoteTariffs(cart) {
    const items = (cart && cart.items) || [];
    if (!items.length) { rfdPaintTariffs(null, cart); return; }
    const sig = rfdTariffCartSig(cart);
    if (sig === rfdTariffSig && rfdTariffQuote) { rfdPaintTariffs(rfdTariffQuote, cart); return; }
    document.querySelectorAll('[data-tariff-recap]').forEach((el) => {
      el.hidden = false;
      el.innerHTML = '<div class="rfd-tariff-recap rfd-tariff-recap--loading">Calculating tariffs…</div>';
    });
    let q = null;
    try {
      const res = await fetch('/apps/rfd-tools/tariffs/quote', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          country: 'US',
          currency: cart.currency,
          cartDiscountCents: Math.max(0, (cart.items_subtotal_price || 0) - (cart.total_price || 0)),
          lines: items.map((i) => ({ key: i.key, variantId: i.variant_id, productId: i.product_id, quantity: i.quantity, finalLinePrice: i.final_line_price, title: i.product_title, sku: i.sku })),
        }),
      });
      q = await res.json();
    } catch (_) { q = null; }
    if (q && q.enabled) { rfdTariffSig = sig; rfdTariffQuote = q; }
    rfdPaintTariffs(q, cart);
  }

  function rfdPaintTariffs(q, cart) {
    const byKey = {};
    ((q && q.lines) || []).forEach((l) => { byKey[l.key] = l; });
    document.querySelectorAll('[data-tariff-line]').forEach((el) => {
      const l = byKey[el.getAttribute('data-tariff-line')];
      el.innerHTML = l && l.surchargeCents > 0
        ? `<span class="rfd-tariff-pct">+${rfdPct(l.pct)}%</span> <span class="rfd-cart-line__tariff-amt">${rfdMoneyFormat(l.surchargeCents)}</span>`
        : '';
    });
    document.querySelectorAll('[data-tariff-recap]').forEach((el) => {
      if (!q || !q.enabled) {
        el.hidden = !q || !q.error;
        el.innerHTML = q && q.error ? '<div class="rfd-tariff-recap rfd-tariff-recap--loading">Tariffs will be calculated at checkout.</div>' : '';
        return;
      }
      if (!(q.totalCents > 0)) { el.hidden = true; el.innerHTML = ''; return; }
      const groups = (q.groups || []).map((g) => `
        <div class="rfd-tariff-recap__group">
          <span>${rfdEsc(g.originName)} · HS ${rfdEsc(g.hsLabel)} · ${rfdPct(g.pct)}%${g.source === 'rate' ? '' : '*'} of ${rfdMoneyFormat(g.baseCents)}</span>
          <span>${rfdMoneyFormat(g.surchargeCents)}</span>
        </div>`).join('');
      el.hidden = false;
      el.innerHTML = `
        <div class="rfd-tariff-recap">
          <div class="rfd-tariff-recap__row rfd-tariff-recap__row--tariff"><span>Tariffs (prepaid)</span><span class="rfd-tariff-recap__avg">${rfdTariffAvgPct(cart, q)}</span><span>+${rfdMoneyFormat(q.totalCents)}</span></div>
          <div class="rfd-tariff-recap__row rfd-tariff-recap__row--total"><span>Total with tariffs</span><span>${rfdMoneyFormat((cart.total_price || 0) + q.totalCents)}</span></div>
          <details class="rfd-tariff-recap__details">
            <summary>Tariff breakdown by origin</summary>
            <div class="rfd-tariff-recap__scroll">
              <div class="rfd-tariff-recap__groups">${groups}</div>
              <p class="rfd-tariff-recap__how">Each item's tariff is its price after discounts × the US tariff rate for its HS code and country of origin, rounded to the cent.${q.usesFallback ? ' * No specific rate on file yet — the standard rate applies.' : ''} The total is added at checkout as one line.</p>
            </div>
          </details>
          <div class="rfd-tariff-ddp">
            <strong>DDP SHIPMENT - PRE-PAID DUTIES &amp; TARIFFS</strong>
            <span>Duties and tariffs are paid upfront — nothing more to pay on delivery.</span>
          </div>
        </div>`;
    });
  }

  function rfdOpenDrawer() {
    const drawer = document.getElementById('rfd-cart-drawer');
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    rfdFetchCart().then(c => { rfdUpdateCount(c); rfdRenderCart(c); });
  }
  function rfdCloseDrawer() {
    const drawer = document.getElementById('rfd-cart-drawer');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ----------------------------------------------------------
     Auto-marketing: add/remove the $0 catalogue + brand samples
     based on the app's eligibility logic. Runs on load + after any
     cart change (debounced), reconciling the cart against the
     /apps/rfd-tools/marketing/cart-eval endpoint. Only touches
     lines we auto-added (properties._rfd_auto). Fails silently.
     ---------------------------------------------------------- */
  // Persist a manual removal of an auto line for THIS cart (a cart attribute,
  // so it clears on the next cart/order). autoVal = "catalogue" | "sample:<key>".
  async function rfdAddDismissal(autoVal) {
    try {
      const cart = rfdCartCache || (await rfdFetchCart());
      const cur = (cart.attributes && cart.attributes._rfd_dismissed) || '';
      const set = new Set(String(cur).split(',').map((s) => s.trim()).filter(Boolean));
      set.add(autoVal);
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { _rfd_dismissed: [...set].join(',') } }),
      });
    } catch (_) { /* ignore */ }
  }

  function rfdParseDismissed(attrs) {
    const raw = (attrs && attrs._rfd_dismissed) || '';
    const out = { catalogue: false, samples: [] };
    String(raw).split(',').map((s) => s.trim()).filter(Boolean).forEach((p) => {
      if (p === 'catalogue') out.catalogue = true;
      else if (p.indexOf('sample:') === 0) out.samples.push(p.slice(7));
    });
    return out;
  }

  function initMarketingAutoCart() {
    const cidEl = document.querySelector('[data-customer-id]');
    if (!cidEl || !cidEl.getAttribute('data-customer-id')) return; // logged-out → never eval

    let running = false;
    let queued = false;

    async function runEval() {
      if (running) { queued = true; return; }
      running = true;
      try {
        const cart = await rfdFetchCart();
        if (!cart || !cart.items || !cart.items.length) return; // empty cart → nothing to do
        const productSubtotal = cart.items
          .filter((it) => !(it.properties && it.properties._rfd_auto))
          .reduce((s, it) => s + (it.final_line_price || 0), 0);
        const dismissed = rfdParseDismissed(cart.attributes);
        const r = await fetch('/apps/rfd-tools/marketing/cart-eval', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            lines: cart.items.map((it) => ({
              variantId: String(it.variant_id),
              productId: String(it.product_id),
              quantity: it.quantity,
              lineSubtotalCents: it.final_line_price,
              properties: it.properties || {},
              key: it.key,
            })),
            cartProductSubtotalCents: productSubtotal,
            dismissed,
            // Which market the cart is priced in: US carts only get samples
            // for ranges the US market carries.
            currency: cart.currency || null,
          }),
        });
        if (!r.ok) return;
        const data = await r.json();
        const toAdd = (data && data.toAdd) || [];
        const toRemove = (data && data.toRemove) || [];
        let changed = false;
        // Remove first (always our own _rfd_auto lines) — direct fetch so we
        // don't re-trigger the cart-updated event (no loop).
        for (const key of toRemove) {
          try {
            const rr = await fetch('/cart/change.js', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 }),
            });
            if (rr.ok) changed = true;
          } catch (_) { /* ignore */ }
        }
        for (const a of toAdd) {
          try {
            const rr = await fetch('/cart/add.js', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: Number(a.variantId), quantity: a.quantity || 1, properties: a.properties || {} }),
            });
            if (rr.ok) changed = true;
          } catch (_) { /* ignore */ }
        }
        if (changed) {
          // Cart page rows are server-rendered → reload to reflect changes.
          // (Idempotent: the next load's eval makes no change, so no reload loop.)
          if (document.querySelector('.rfd-cart-page__row')) { window.location.reload(); return; }
          const c = await rfdFetchCart();
          rfdUpdateCount(c);
          rfdRenderCart(c);
          rfdSyncCardsFromCart(c);
        }
      } catch (_) { /* never break the cart */ }
      finally {
        running = false;
        if (queued) { queued = false; runEval(); }
      }
    }

    let t = null;
    document.addEventListener('rfd:cart-updated', () => { clearTimeout(t); t = setTimeout(runEval, 300); });
    runEval(); // on load
  }

  function initCartForms() {
    // Add / Adjust cart
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('.rfd-atc-form');
      if (!form) return;
      e.preventDefault();

      // Refuse to add when the submit button is disabled (out of stock)
      const submitBtnCheck = form.querySelector('button[type="submit"]');
      if (submitBtnCheck && (submitBtnCheck.disabled || submitBtnCheck.getAttribute('aria-disabled') === 'true')) {
        return;
      }

      const productName = form.dataset.productName || 'Item';
      const qty         = parseInt(form.querySelector('[name="quantity"]')?.value || 1);
      const submitBtn   = form.querySelector('button[type="submit"]');

      // Subtle click feedback on the Add button
      if (submitBtn) {
        submitBtn.classList.add('rfd-atc-added');
        setTimeout(() => submitBtn.classList.remove('rfd-atc-added'), 600);
      }

      const mode = form.dataset.mode || 'add';

      try {
        let resp, ok = false;
        if (mode === 'adjust' && form.dataset.lineKey) {
          // Overwrite the cart line quantity
          resp = await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: form.dataset.lineKey, quantity: qty })
          });
          ok = resp.ok;
          if (ok) window.rfdToast(`${productName} updated to ×${qty}.`);
        } else {
          const data = new FormData(form);
          resp = await fetch('/cart/add.js', { method: 'POST', body: data, credentials: 'same-origin' });
          ok = resp.ok;
          if (ok) window.rfdToast(`${productName} ×${qty} added.`);
        }

        if (ok) {
          const cart = await rfdFetchCart();
          rfdUpdateCount(cart);
          rfdRenderCart(cart);
          rfdSyncCardsFromCart(cart);
          document.dispatchEvent(new CustomEvent('rfd:cart-updated'));
          // Skip auto-opening the drawer for list view + adjustments
          if (!form.hasAttribute('data-no-drawer') && mode !== 'adjust') {
            rfdOpenDrawer();
          }
        } else {
          window.rfdToast('Could not update cart. Try again.');
        }
      } catch (err) {
        window.rfdToast('Could not update cart. Try again.');
      }
    });

    // Discount codes (drawer + cart page)
    document.addEventListener('click', (e) => {
      const apply = e.target.closest('[data-discount-apply]');
      if (apply) { e.preventDefault(); rfdApplyDiscount(apply.closest('[data-discount]')); return; }
      const rm = e.target.closest('[data-discount-remove]');
      if (rm) { e.preventDefault(); rfdRemoveDiscount(rm.getAttribute('data-discount-remove')); }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || !e.target.matches || !e.target.matches('[data-discount-input]')) return;
      e.preventDefault(); // inside the cart page form: never submit it
      rfdApplyDiscount(e.target.closest('[data-discount]'));
    });

    // Pre-order top-up: add the cart to the order already holding their stock
    document.addEventListener('click', (e) => {
      const add = e.target.closest('[data-topup-add]');
      if (add) {
        e.preventDefault();
        rfdTopupSubmit(add);
        return;
      }
      const x = e.target.closest('[data-topup-dismiss]');
      if (x) {
        try { sessionStorage.setItem('rfd-topup-hide', '1'); } catch (_) {}
        const bar = document.getElementById('rfd-topup-bar');
        if (bar) bar.remove();
      }
    });
    rfdLoadTopup();
    document.addEventListener('rfd:cart-updated', rfdTopupPaint);

    // Drawer open/close
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        rfdOpenDrawer();
      } else if (e.target.closest('[data-cart-close]')) {
        rfdCloseDrawer();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') rfdCloseDrawer();
    });

    // Clear entire cart (with confirmation) — drawer + cart page
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-cart-clear]');
      if (!btn) return;
      e.preventDefault();
      if (!window.confirm('Remove all items from your cart? This cannot be undone.')) return;
      const onPage = btn.dataset.cartContext === 'page';
      btn.disabled = true;
      try {
        await rfdClearCart();
        if (onPage) { window.location.reload(); return; }
        window.rfdToast('Cart cleared.');
      } catch (err) {
        window.rfdToast('Could not clear the cart. Try again.');
        btn.disabled = false;
      }
    });

    // Line item interactions (drawer + cart page) — buttons
    document.addEventListener('click', async (e) => {
      const line = e.target.closest('.rfd-cart-line, .rfd-cart-page__row');
      if (!line) return;
      const isCartPage = line.classList.contains('rfd-cart-page__row');
      const key = line.dataset.lineKey;
      const inc = parseInt(line.dataset.increment || '1');
      const qtyEl = line.querySelector('[data-line-qty]');
      const isInput = qtyEl && qtyEl.tagName === 'INPUT';
      const current = parseInt(isInput ? qtyEl.value : (qtyEl?.textContent || '1'));
      const minQty = parseInt(line.dataset.minQty || inc);

      let action = null;
      if (e.target.closest('[data-line-remove]')) action = 'remove';
      else if (e.target.closest('[data-line-inc]')) action = 'inc';
      else if (e.target.closest('[data-line-dec]')) action = 'dec';
      if (!action) return;

      let nextQty;
      if (action === 'remove') {
        nextQty = 0;
        // Marketing: removing an auto-added line → remember it for this cart.
        try {
          const lines = (rfdCartCache && rfdCartCache.items) || [];
          const ln = lines.find((x) => x.key === key);
          const autoVal = ln && ln.properties && ln.properties._rfd_auto;
          if (autoVal) await rfdAddDismissal(autoVal);
        } catch (_) { /* ignore */ }
      } else if (action === 'inc') nextQty = current + inc;
      else {
        nextQty = current - inc;
        if (nextQty < minQty) nextQty = 0;
      }

      try {
        await rfdChangeLine(key, nextQty);
        if (isCartPage) window.location.reload();
      } catch (err) {
        window.rfdToast('Could not update cart.');
      }
    });

    // Line item interactions — manual qty input (commit on Enter or blur)
    document.addEventListener('change', async (e) => {
      const inp = e.target;
      if (!(inp instanceof HTMLInputElement)) return;
      if (!inp.matches('[data-line-qty]')) return;
      const line = inp.closest('.rfd-cart-line, .rfd-cart-page__row');
      if (!line) return;
      const isCartPage = line.classList.contains('rfd-cart-page__row');
      const key = line.dataset.lineKey;
      const inc = parseInt(line.dataset.increment || '1') || 1;
      const minQty = parseInt(line.dataset.minQty || inc);
      let val = parseInt(inp.value);
      if (isNaN(val) || val < 0) val = minQty;
      // Snap to nearest valid multiple (>= minQty) unless removing (0)
      if (val > 0 && inc > 1) {
        const stepsFromBase = Math.round((val - minQty) / inc);
        val = minQty + Math.max(0, stepsFromBase) * inc;
      }
      if (val > 0 && val < minQty) val = minQty;
      inp.value = val;
      try {
        await rfdChangeLine(key, val);
        if (isCartPage) window.location.reload();
      } catch (err) {
        window.rfdToast('Could not update cart.');
      }
    });

    // Enter key on qty input → blur (which triggers change handler)
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const inp = e.target;
      if (!(inp instanceof HTMLInputElement)) return;
      if (!inp.matches('[data-line-qty]')) return;
      e.preventDefault();
      inp.blur();
    });

    // Double-click on any qty input selects all text for quick overwrite
    document.addEventListener('dblclick', (e) => {
      const inp = e.target;
      if (!(inp instanceof HTMLInputElement)) return;
      if (!inp.matches('[data-line-qty], [data-stepper-val]')) return;
      inp.select();
    });

    // Initial cart count on page load + sync product cards if any
    rfdFetchCart().then(c => {
      rfdUpdateCount(c);
      rfdSyncCardsFromCart(c);
      if (document.getElementById('rfd-cart-page')) rfdCartExtras(c);
    }).catch(() => {});
  }

  /* ============================================================
     PRODUCT PAGE — Image carousel (slide index)
     ============================================================ */
  function initProductCarousel() {
    const carousel = $('#rfd-carousel');
    if (!carousel) return;

    const slides     = $$('[data-slide]', carousel);
    const thumbs     = $$('[data-thumb]');
    const prevBtn    = $('#rfd-carousel-prev');
    const nextBtn    = $('#rfd-carousel-next');
    const counterEl  = $('#rfd-carousel-counter');
    const total      = slides.length;
    let   current    = 0;

    function goTo(n) {
      current = (n + total) % total;
      slides.forEach((s, i) => s.style.display = i === current ? 'flex' : 'none');
      thumbs.forEach((t, i) => t.classList.toggle('active', i === current));
      if (counterEl) counterEl.textContent = `${current + 1} / ${total}`;
    }

    if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));
    thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));

    goTo(0);

    // ── Hover-to-zoom on the main image (uses background-image + transform) ──
    slides.forEach(slide => {
      const img = slide.querySelector('.rfd-product__main-img');
      if (!img) return;
      const zoomSrc = img.dataset.zoomSrc || img.src;

      slide.addEventListener('mousemove', (e) => {
        if (!slide.classList.contains('zooming')) {
          slide.classList.add('zooming');
          slide.style.backgroundImage = `url("${zoomSrc}")`;
          slide.style.backgroundRepeat = 'no-repeat';
          slide.style.backgroundColor = '#fff';
          img.style.opacity = '0';
        }
        const rect = slide.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        slide.style.backgroundPosition = `${x}% ${y}%`;
        slide.style.backgroundSize = '200%';
      });
      slide.addEventListener('mouseleave', () => {
        slide.classList.remove('zooming');
        slide.style.backgroundImage = '';
        img.style.opacity = '1';
      });
    });

    // ── Lightbox on click ──
    const lightbox = document.getElementById('rfd-lightbox');
    if (!lightbox) return;
    // <main> keeps a transform from its entrance animation (fill-mode: forwards),
    // and a transformed ancestor becomes the containing block for position:fixed.
    // Left inside <main>, the overlay spanned the full page height and centred the
    // image far below the fold. Re-parent to <body> so it pins to the viewport —
    // the same fix the samples modal and scroll-to-top button already use.
    if (lightbox.parentElement !== document.body) document.body.appendChild(lightbox);
    const lbImg     = lightbox.querySelector('.rfd-lightbox__img');
    const lbCounter = lightbox.querySelector('[data-lightbox-counter]');
    let lbIndex = 0;

    function openLb(i) {
      lbIndex = (i + total) % total;
      const img = slides[lbIndex].querySelector('.rfd-product__main-img');
      if (!img) return;
      lbImg.src = img.dataset.fullSrc || img.dataset.zoomSrc || img.src;
      if (lbCounter) lbCounter.textContent = `${lbIndex + 1} / ${total}`;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeLb() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    slides.forEach((slide, i) => {
      slide.addEventListener('click', (e) => {
        // Only open on click that's not part of a drag/zoom-end
        if (e.target.closest('.rfd-product__carousel-nav')) return;
        openLb(i);
      });
    });

    lightbox.addEventListener('click', (e) => {
      if (e.target.closest('[data-lightbox-close]')) closeLb();
      else if (e.target.closest('[data-lightbox-prev]')) openLb(lbIndex - 1);
      else if (e.target.closest('[data-lightbox-next]')) openLb(lbIndex + 1);
      else if (e.target === lightbox) closeLb();
    });
    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape')      closeLb();
      else if (e.key === 'ArrowLeft')  openLb(lbIndex - 1);
      else if (e.key === 'ArrowRight') openLb(lbIndex + 1);
    });
  }

  /* ============================================================
     PRODUCT PAGE — Tab switcher (Description / Details)
     ============================================================ */
  function initProductTabs() {
    const tabBtns  = $$('[data-tab-btn]');
    const tabPanes = $$('[data-tab-pane]');
    if (!tabBtns.length) return;

    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tabBtn;
        tabBtns.forEach(b => b.classList.toggle('active', b.dataset.tabBtn === target));
        tabPanes.forEach(p => p.style.display = p.dataset.tabPane === target ? 'block' : 'none');
      });
    });
  }

  /* ============================================================
     HOME — Scroll to section buttons
     ============================================================ */
  function initScrollAnchors() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-scroll-to]');
      if (!btn) return;
      const target = document.getElementById(btn.dataset.scrollTo);
      if (target) window.scrollTo({ top: target.offsetTop - 80, behavior: 'smooth' });
    });
  }

  /* ============================================================
     reCAPTCHA helper — Shopify injects invisible reCAPTCHA v3 on
     contact forms. We need an explicit token when submitting via fetch.
     ============================================================ */
  function rfdGetCaptchaToken(action) {
    return new Promise((resolve) => {
      const scriptEl = document.querySelector('script[src*="recaptcha/api.js"]');
      const m = scriptEl ? scriptEl.src.match(/[?&]render=([^&]+)/) : null;
      const siteKey = m ? m[1] : null;
      if (!siteKey || typeof window.grecaptcha === 'undefined') {
        resolve(null);
        return;
      }
      try {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(siteKey, { action: action || 'contact_form' })
            .then(resolve)
            .catch(() => resolve(null));
        });
      } catch (e) {
        resolve(null);
      }
    });
  }

  /* ============================================================
     REQUEST SAMPLE FORM
     Brand picker toggle + form submit with success state
     ============================================================ */
  function initSampleForm() {
    const form = document.querySelector('#rfd-sample-form, form[id="rfd-sample-form"]')
              || document.querySelector('#rfd-request-sample form');
    if (!form) return;
    if (form.dataset.rfdInit === '1') return;
    form.dataset.rfdInit = '1';

    const brandBtns    = $$('.rfd-sample__brand-btn', form);
    const brandsHidden = form.querySelector('#rfd-sample-brands-hidden') || $('#rfd-sample-brands-hidden');
    const successEl    = $('#rfd-sample-success');

    if (/[?&]contact_posted=true/.test(window.location.search)) {
      if (successEl) successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const url = new URL(window.location.href);
      url.searchParams.delete('contact_posted');
      url.searchParams.delete('form_type');
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }

    const selected = new Set();
    brandBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.brandId;
        if (selected.has(id)) { selected.delete(id); btn.classList.remove('selected'); }
        else                  { selected.add(id);    btn.classList.add('selected'); }
      });
    });

    const submitBtn = form.querySelector('#rfd-sample-submit') || form.querySelector('button[type="submit"]');

    form.addEventListener('submit', (e) => {
      if (selected.size === 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        window.rfdToast('Please select at least one brand.');
        return false;
      }
      const brandNames = [...brandBtns]
        .filter(b => selected.has(b.dataset.brandId))
        .map(b => b.dataset.brandName);

      const storeEl = form.querySelector('#rfd-sample-store');
      const notesEl = form.querySelector('#rfd-sample-notes');
      const storeName = storeEl ? storeEl.value.trim() : '';
      const notes     = notesEl ? notesEl.value.trim() : '';

      // Remove non-standard contact[] fields Shopify rejects
      ['contact[store_name]', 'contact[brands]'].forEach(n => {
        const el = form.querySelector(`[name="${n}"]`);
        if (el) el.removeAttribute('name');
      });

      const subjEl = form.querySelector('input[name="contact[subject]"]');
      if (subjEl) subjEl.value = 'Sample Request — ' + (storeName || 'New') + ' (' + brandNames.join(', ') + ')';

      let nameEl = form.querySelector('input[name="contact[name]"]');
      if (!nameEl) {
        nameEl = document.createElement('input');
        nameEl.type = 'hidden';
        nameEl.name = 'contact[name]';
        form.appendChild(nameEl);
      }
      nameEl.value = storeName;

      if (notesEl) {
        notesEl.value =
          '[Sample Request]\n' +
          'Store: ' + storeName + '\n' +
          'Brands: ' + brandNames.join(', ') + '\n\n' +
          'Notes: ' + (notes || '(none)');
      }

      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }
    }, true);
  }

  /* ============================================================
     BRAND SUGGESTION FORM
     ============================================================ */
  function initSuggestForm() {
    const root = document.querySelector('[data-suggest-form]');
    if (!root || root.dataset.rfdInit === '1') return;
    root.dataset.rfdInit = '1';

    const btn = root.querySelector('[data-suggest-submit]');
    const successEl = root.querySelector('[data-suggest-success]');
    const fieldsEl = root.querySelector('[data-suggest-fields]');
    const errEl = root.querySelector('[data-suggest-error]');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const brandEl = root.querySelector('[data-suggest-brand]');
      const webEl = root.querySelector('[data-suggest-website]');
      const noteEl = root.querySelector('[data-suggest-note]');
      const name = (brandEl && brandEl.value || '').trim();
      if (errEl) { errEl.style.display = 'none'; errEl.textContent = ''; }
      if (!name) { if (errEl) { errEl.textContent = 'Enter a brand name.'; errEl.style.display = 'block'; } return; }

      btn.disabled = true; btn.textContent = 'Sending…';
      try {
        const r = await fetch('/apps/rfd-tools/brands/suggest', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            brand_name: name,
            website: (webEl && webEl.value || '').trim(),
            note: (noteEl && noteEl.value || '').trim(),
            source: 'home',
            customer_email: root.dataset.customerEmail || '',
            customer_name: root.dataset.customerName || '',
          }),
        });
        if (r.ok) {
          if (fieldsEl) fieldsEl.style.display = 'none';
          if (successEl) { successEl.style.display = 'block'; successEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        } else {
          if (errEl) { errEl.textContent = 'Could not submit. Try again.'; errEl.style.display = 'block'; }
          btn.disabled = false; btn.textContent = 'Suggest brand';
        }
      } catch (err) {
        if (errEl) { errEl.textContent = 'Could not submit. Try again.'; errEl.style.display = 'block'; }
        btn.disabled = false; btn.textContent = 'Suggest brand';
      }
    });
  }

  /* ============================================================
     CONTACT FORM
     ============================================================ */
  function initContactForm() {
    const form = $('#rfd-contact-form');
    if (!form) return;
    /* Dawn handles native Shopify contact form — we just need JS for focus styles,
       which are handled via CSS :focus selectors. Nothing extra needed. */
  }

  /* ============================================================
     APPLY / LOGIN PAGE — Tab switcher + form states
     ============================================================ */
  function initApplyPage() {
    const page = $('#rfd-apply-page');
    if (!page) return;

    const tabBtns      = $$('.rfd-apply__tab-btn',  page);
    const applyPanel   = $('#rfd-apply-panel');
    const loginPanel   = $('#rfd-login-panel');
    const applySubmit  = $('#rfd-apply-submit');
    const applySuccess = $('#rfd-apply-success');
    const applyForm    = $('#rfd-apply-form');

    function switchTab(tab) {
      tabBtns.forEach(b => b.classList.toggle('active', b.dataset.tab === tab));
      if (applyPanel) applyPanel.style.display = tab === 'apply' ? 'block' : 'none';
      if (loginPanel) loginPanel.style.display = tab === 'login' ? 'block' : 'none';
    }

    tabBtns.forEach(btn => btn.addEventListener('click', () => switchTab(btn.dataset.tab)));

    /* Cross-links inside panels */
    $$('[data-switch-tab]', page).forEach(el => {
      el.addEventListener('click', () => switchTab(el.dataset.switchTab));
    });

    /* Apply form submit */
    if (applySubmit) {
      applySubmit.addEventListener('click', () => {
        const biz   = $('#rfd-apply-biz')?.value?.trim();
        const email = $('#rfd-apply-email')?.value?.trim();
        if (!biz || !email) { window.rfdToast('Please fill in required fields.'); return; }
        if (applyForm)    applyForm.style.display    = 'none';
        if (applySuccess) applySuccess.style.display = 'block';
      });
    }
  }

  /* ============================================================
     COPY LINK — any [data-copy-url] button copies its URL, shows a green
     checkmark for 2s, then reverts. Used by the brand image folders.
     ============================================================ */
  function initCopyLinks() {
    const CHECK = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
    async function copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          return;
        } catch (_) { /* permission refused — fall through to the classic copy */ }
      }
      // Fallback: browsers without the async clipboard API, or where it was refused.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      if (!ok) throw new Error('copy failed');
    }
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-copy-url]');
      if (!btn) return;
      e.preventDefault();
      try { await copyText(btn.dataset.copyUrl); } catch (_) { return; }
      if (!btn.dataset.copyLabel) {
        btn.dataset.copyLabel = btn.innerHTML;
        btn.dataset.copyAria = btn.getAttribute('aria-label') || '';
      }
      btn.style.minWidth = btn.offsetWidth + 'px'; // keep its width while showing the check
      btn.innerHTML = CHECK;
      btn.classList.add('is-copied');
      btn.setAttribute('aria-label', 'Link copied');
      clearTimeout(btn._copyTimer);
      btn._copyTimer = setTimeout(() => {
        btn.innerHTML = btn.dataset.copyLabel;
        btn.classList.remove('is-copied');
        btn.setAttribute('aria-label', btn.dataset.copyAria);
        btn.style.minWidth = '';
      }, 2000);
    });
  }

  /* ============================================================
     RESOURCES PAGE — Client-side brand search
     ============================================================ */
  function initResourcesSearch() {
    const searchInput = $('#rfd-resources-search');
    if (searchInput) {
      const rows   = $$('.rfd-resource-row[data-brand-name]');
      const empty  = $('#rfd-resources-empty');
      searchInput.addEventListener('input', () => {
        const q = searchInput.value.trim().toLowerCase();
        let   count = 0;
        rows.forEach(row => {
          const match = !q || row.dataset.brandName.toLowerCase().includes(q);
          row.style.display = match ? '' : 'none';
          if (match) count++;
        });
        if (empty) empty.style.display = count === 0 ? 'block' : 'none';
      });
    }

    // Tab switching for Resources HUB
    const hub = document.querySelector('[data-resources-hub]');
    if (!hub) return;
    const tabs   = hub.querySelectorAll('[data-tab-target]');
    const panels = hub.querySelectorAll('[data-tab-panel]');
    const searchWrap = hub.querySelector('[data-resources-search-wrap]');

    function showTab(target) {
      tabs.forEach(t => {
        const active = t.dataset.tabTarget === target;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      panels.forEach(p => {
        const active = p.dataset.tabPanel === target;
        p.classList.toggle('is-active', active);
        if (active) p.removeAttribute('hidden');
        else p.setAttribute('hidden', '');
      });
      // Brand search is only meaningful on the Brand Files tab
      if (searchWrap) searchWrap.style.visibility = target === 'brand-files' ? '' : 'hidden';
    }

    tabs.forEach(t => {
      t.addEventListener('click', () => showTab(t.dataset.tabTarget));
    });

    // Brand Files: "Access images" menus — one open at a time, closed by an
    // outside click, Escape, or picking an item.
    const setDd = (dd, open) => {
      dd.classList.toggle('is-open', open);
      const btn = dd.querySelector('[data-res-dd-toggle]');
      const menu = dd.querySelector('.rfd-res-dd__menu');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (menu) menu.hidden = !open;
    };
    document.addEventListener('click', (e) => {
      const openOnes = hub.querySelectorAll('[data-res-dd].is-open');
      const toggle = e.target.closest('[data-res-dd-toggle]');
      if (toggle) {
        const dd = toggle.closest('[data-res-dd]');
        const willOpen = !dd.classList.contains('is-open');
        openOnes.forEach(d => setDd(d, false));
        if (willOpen) setDd(dd, true);
        return;
      }
      if (!e.target.closest('[data-res-dd]') || e.target.closest('.rfd-res-dd__item')) {
        openOnes.forEach(d => setDd(d, false));
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') hub.querySelectorAll('[data-res-dd].is-open').forEach(d => setDd(d, false));
    });
  }

  /* ============================================================
     HERO VARIANT — injected via section setting
     ============================================================ */
  function initHeroVariant() {
    const hero = $('#rfd-hero-section');
    if (!hero) return;
    /* Variant is already set via inline style from Liquid */
  }

  /* ============================================================
     NAV — active link highlight based on current URL
     ============================================================ */
  function initNavHighlight() {
    const links = $$('.rfd-nav__link');
    const path  = window.location.pathname;
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      if (path === href || (href !== '/' && path.startsWith(href))) {
        link.classList.add('rfd-nav__link--active');
      }
    });
  }

  /* ============================================================
     INIT ALL
     ============================================================ */
  /* ============================================================
     ORDER IMPORT — CSV to cart (SKU, QTY)
     ============================================================ */
  function initOrderImport() {
    const modal     = document.getElementById('rfd-import-modal');
    if (!modal) return;
    const fileInput = document.getElementById('rfd-import-file');
    const dropZone  = document.getElementById('rfd-import-drop');
    const submitBtn = document.getElementById('rfd-import-submit');
    const resultEl  = document.getElementById('rfd-import-result');
    const dropText  = modal.querySelector('.rfd-import__drop-text');
    const stepsEl   = modal.querySelector('.rfd-import__steps');
    const cancelBtn = modal.querySelector('.rfd-import__actions [data-import-close]');
    let parsedRows = []; // [{sku, qty, loc?}]
    let lastCsvText = '';

    // Toggle between the upload view and the confirmation recap view.
    function showUploadView() {
      if (stepsEl) stepsEl.style.display = '';
      if (dropZone) dropZone.style.display = '';
      if (cancelBtn) cancelBtn.textContent = 'Cancel';
    }
    function showRecapView() {
      if (stepsEl) stepsEl.style.display = 'none';
      if (dropZone) dropZone.style.display = 'none';
      if (cancelBtn) cancelBtn.textContent = 'Close';
    }

    // Progress bar — created once, inserted above the result area.
    const progressWrap = document.createElement('div');
    progressWrap.className = 'rfd-import__progress';
    progressWrap.style.display = 'none';
    progressWrap.innerHTML = '<div class="rfd-import__progress-label"></div>'
      + '<div class="rfd-import__progress-track"><div class="rfd-import__progress-bar"></div></div>';
    if (resultEl && resultEl.parentNode) resultEl.parentNode.insertBefore(progressWrap, resultEl);
    const progressLabel = progressWrap.querySelector('.rfd-import__progress-label');
    const progressBar   = progressWrap.querySelector('.rfd-import__progress-bar');
    function setProgress(done, total, label) {
      progressWrap.style.display = 'block';
      const pct = total ? Math.round((done / total) * 100) : 0;
      progressBar.style.width = pct + '%';
      progressLabel.textContent = label || `Adding to cart… ${done} / ${total}`;
    }
    function hideProgress() { progressWrap.style.display = 'none'; }

    function openModal() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    let pendingDrawerOpen = false;
    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      parsedRows = [];
      submitBtn.disabled = true;
      submitBtn.style.display = '';
      submitBtn.textContent = 'Add to Cart';
      resultEl.style.display = 'none';
      resultEl.innerHTML = '';
      if (fileInput) fileInput.value = '';
      dropText.textContent = 'Click to choose a CSV file';
      hideProgress();
      showUploadView();
      submitMode = 'submit';
      if (pendingDrawerOpen) {
        pendingDrawerOpen = false;
        // Slight delay so modal close animation finishes before drawer slides in
        setTimeout(() => rfdOpenDrawer(), 200);
      }
    }

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-import-open]'))  { e.preventDefault(); openModal(); }
      else if (e.target.closest('[data-import-close]')) closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    // Drag & drop
    ['dragover','dragenter'].forEach(evt => {
      dropZone.addEventListener(evt, (e) => { e.preventDefault(); dropZone.classList.add('is-drag'); });
    });
    ['dragleave','drop'].forEach(evt => {
      dropZone.addEventListener(evt, (e) => { e.preventDefault(); dropZone.classList.remove('is-drag'); });
    });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      const f = e.dataTransfer?.files?.[0];
      if (f) handleFile(f);
    });
    fileInput.addEventListener('change', () => {
      const f = fileInput.files?.[0];
      if (f) handleFile(f);
    });

    function looksBinary(text) {
      // Any NUL bytes? Definitely binary.
      if (text.indexOf(' ') !== -1) return true;
      // Sample first 2KB. Count chars outside printable ASCII / common whitespace.
      const sample = text.slice(0, 2048);
      let nonText = 0;
      for (let i = 0; i < sample.length; i++) {
        const c = sample.charCodeAt(i);
        // Allow tab(9), LF(10), CR(13), printable ASCII (32-126), and common Latin-1 / UTF-8 continuation
        if (c === 9 || c === 10 || c === 13) continue;
        if (c >= 32 && c <= 126) continue;
        if (c >= 160) continue; // higher Unicode is fine
        nonText++;
      }
      return nonText / Math.max(sample.length, 1) > 0.1;
    }

    function parseCSV(text) {
      // Strip BOM, normalize line endings, ignore blank lines
      const clean = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
      const lines = clean.split('\n').map(l => l.trim()).filter(l => l.length);
      const cells = (l) => l.split(/[,;]/).map(p => p.trim().replace(/^"|"$/g, ''));
      const rows = [];
      const hasHeader = /sku/i.test(lines[0] || '');
      const head = hasHeader ? cells(lines[0]).map(h => h.toLowerCase()) : [];
      const find = (re) => head.findIndex(h => re.test(h));
      const skuI = hasHeader ? Math.max(0, find(/^sku$/)) : 0;
      const qtyI = hasHeader ? find(/^(qty|quantity|quantité|quantite|qte)$/) : 1;
      const locI = hasHeader ? find(/^(store|store code|location|location code|door|magasin|succursale|code magasin)$/) : -1;

      // Multi-store, one row per SKU and store: SKU, QTY, STORE.
      if (locI >= 0 && qtyI >= 0) {
        for (let i = 1; i < lines.length; i++) {
          const c = cells(lines[i]);
          const qty = parseInt(c[qtyI]);
          if (c[skuI] && c[locI] && qty > 0) rows.push({ sku: c[skuI].toLowerCase(), qty, loc: c[locI].toUpperCase() });
        }
        return rows;
      }
      // Multi-store, one column per store: SKU, MTL-01, QC-02, …
      if (hasHeader && qtyI < 0 && head.length > 2) {
        const codes = cells(lines[0]);
        for (let i = 1; i < lines.length; i++) {
          const c = cells(lines[i]);
          if (!c[skuI]) continue;
          codes.forEach((code, j) => {
            if (j === skuI || !code) return;
            const qty = parseInt(c[j]);
            if (qty > 0) rows.push({ sku: c[skuI].toLowerCase(), qty, loc: code.toUpperCase() });
          });
        }
        return rows;
      }
      // Single destination: SKU, QTY.
      for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
        const c = cells(lines[i]);
        const qty = parseInt(c[qtyI >= 0 ? qtyI : 1]);
        if (c[skuI] && qty > 0) rows.push({ sku: c[skuI].toLowerCase(), qty });
      }
      return rows;
    }

    function parsePlain(text) {
      const lines = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').split('\n').map(l => l.trim()).filter(Boolean);
      const rows = [];
      for (let i = /sku/i.test(lines[0] || '') ? 1 : 0; i < lines.length; i++) {
        const c = lines[i].split(/[,;]/).map(p => p.trim().replace(/^"|"$/g, ''));
        const qty = parseInt(c[1]);
        if (c[0] && qty > 0) rows.push({ sku: c[0].toLowerCase(), qty });
      }
      return rows;
    }

    // ── Multi-store: one paid order, shipped to several of the retailer's stores ──
    // Plan the cart from a per-store file. Per SKU the cart gets the stores'
    // combined quantity; when stock is short, stores are served in the order
    // they appear in the file, and each store's shortfall is reported on its own
    // bounce-back line. Pure (no network) so it can be tested.
    function planMultiStore(rows, skuMap, curQty, storeCodes) {
      const known = new Set(storeCodes);
      const out = { toUpdate: [], already: [], outOfStock: [], partial: [], notFound: [], allocation: {} };
      const bySku = new Map();
      rows.forEach(r => {
        if (!known.has(r.loc)) {
          out.notFound.push({ sku: r.sku, qty: r.qty, title: (skuMap[r.sku] || {}).title || '', loc: r.loc, unknownStore: true });
          return;
        }
        if (!bySku.has(r.sku)) bySku.set(r.sku, []);
        bySku.get(r.sku).push(r);
      });
      bySku.forEach((storeRows, sku) => {
        const hit = skuMap[sku];
        if (!hit) { storeRows.forEach(r => out.notFound.push({ sku, qty: r.qty, title: '', loc: r.loc })); return; }
        if (!hit.available || hit.limit === 0) {
          storeRows.forEach(r => out.outOfStock.push({ sku, qty: r.qty, title: hit.title, loc: r.loc, preorder: !!hit.preorder }));
          return;
        }
        let left = hit.limit == null ? Infinity : hit.limit;
        let total = 0;
        let requested = 0;
        storeRows.forEach(r => {
          requested += r.qty;
          const take = Math.max(0, Math.min(r.qty, left));
          left -= take;
          total += take;
          if (take < r.qty) {
            if (take === 0) out.outOfStock.push({ sku, qty: r.qty, title: hit.title, loc: r.loc });
            else out.partial.push({ sku, qty: take, requested: r.qty, title: hit.title, loc: r.loc });
          }
          if (take > 0) {
            if (!out.allocation[r.loc]) out.allocation[r.loc] = [];
            out.allocation[r.loc].push({ variantId: hit.id, sku, qty: take });
          }
        });
        if (!total) return;
        const line = { sku, qty: total, requested, title: hit.title, id: hit.id };
        if ((curQty.get(String(hit.id)) || 0) === total) out.already.push(line);
        else out.toUpdate.push(line);
      });
      return out;
    }

    function showFileError(msg) {
      parsedRows = [];
      submitBtn.disabled = true;
      submitBtn.textContent = 'Add to Cart';
      submitMode = 'submit';
      dropText.textContent = 'Click to choose a CSV file';
      resultEl.style.display = 'block';
      resultEl.className = 'rfd-import__result rfd-import__result--error';
      resultEl.innerHTML = `<strong>${msg}</strong><br><small>Only plain-text CSV files (.csv) are supported. Open your file in Excel or Google Sheets and re-export as <em>Comma-Separated Values (.csv)</em>.</small>`;
      if (fileInput) fileInput.value = '';
    }

    function handleFile(f) {
      // Reject obviously wrong types up front
      const name = (f.name || '').toLowerCase();
      const type = (f.type || '').toLowerCase();
      const isCsvByExt  = /\.csv$/.test(name) || /\.txt$/.test(name) || /\.tsv$/.test(name);
      const isCsvByType = !type || /csv|plain|comma-separated|tab-separated/.test(type);
      if (!isCsvByExt && !isCsvByType) {
        showFileError(`"${f.name}" doesn't look like a CSV file.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const raw = String(reader.result || '');
        if (looksBinary(raw)) {
          showFileError(`"${f.name}" appears to be a binary file (PDF, image, Excel workbook, etc.), not a CSV.`);
          return;
        }
        lastCsvText = raw;
        const rows = parseCSV(raw);
        if (!rows.length) {
          showFileError(`Couldn't read any SKU/quantity rows from "${f.name}".`);
          return;
        }
        parsedRows = rows;
        dropText.textContent = `${f.name} — ${rows.length} row${rows.length === 1 ? '' : 's'} found`;
        submitBtn.disabled = false;
        resultEl.style.display = 'none';
        resultEl.innerHTML = '';
      };
      reader.onerror = () => showFileError(`Could not read "${f.name}".`);
      reader.readAsText(f);
    }

    const sleep = (ms) => new Promise(res => setTimeout(res, ms));

    // GET JSON with 429 backoff. Returns { ok, status, data } (data null if the
    // body isn't JSON — e.g. Shopify's HTML rate-limit page).
    async function getJson(url, tries = 4) {
      for (let attempt = 0; attempt < tries; attempt++) {
        let r;
        try { r = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }); }
        catch (_) { await sleep(600 * (attempt + 1)); continue; }
        if (r.status === 429) { await sleep(1200 * (attempt + 1)); continue; }
        let data = null;
        try { data = await r.json(); } catch (_) {}
        return { ok: r.ok, status: r.status, data };
      }
      return { ok: false, status: 429, data: null };
    }

    // POST /cart/update.js — SET absolute quantities for a map of variantId→qty
    // in ONE request. It adds variants not yet in the cart, updates those that
    // are, and is IDEMPOTENT (setting the current qty is a no-op). That's why a
    // re-import can't double up or trip Shopify's "maximum quantity already in
    // your cart" — the failure this whole flow was hitting. Returns JSON on
    // success and on 422 (with `description`); HTML on 429 (guarded). ONE short
    // retry on 429, then bail — we never grind a rate-limited store.
    async function cartUpdate(updates) {
      for (let attempt = 0; attempt < 2; attempt++) {
        let r;
        try {
          r = await fetch('/cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates })
          });
        } catch (_) { return { ok: false, status: 0, body: null }; }
        if (r.status === 429 && attempt === 0) { await sleep(700); continue; }
        let body = null;
        try { body = await r.json(); } catch (_) {}
        return { ok: r.ok, status: r.status, body };
      }
      return { ok: false, status: 429, body: null };
    }

    // Human reason for a failed single-line add.
    function addFailReason(res) {
      if (res.status === 429) return 'Server busy — try again in a moment';
      if (res.status === 422) {
        const d = res.body && (res.body.description || res.body.message);
        return d ? String(d).replace(/<[^>]*>/g, '') : 'Not enough stock';
      }
      return 'Could not be added';
    }

    // Build SKU → { id, available, title } map. Tries sessionStorage first,
    // otherwise fetches /products.json paginated (with 429 backoff). `available`
    // lets us skip out-of-stock / pre-order variants before the cart add;
    // `title` feeds the confirmation recap. Throws 'catalogue-load-failed' if
    // the catalogue can't be read (so the UI can say so instead of silently
    // treating every SKU as "not found").
    async function buildSkuMap() {
      // v4: values carry stock counts. Two minutes, not ten — the counts decide
      // how much of each line is added, so they must not go stale for long.
      const cacheKey = 'rfd:sku-map:v5'; // v5: barcodes
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.ts && (Date.now() - parsed.ts) < 120000) return parsed.map;
        }
      } catch (_) {}

      let map = null;
      try { map = await stockMapFromView(); } catch (_) { map = null; }
      if (!map) map = await legacySkuMap();
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), map })); } catch (_) {}
      return map;
    }

    // Stock per SKU from the theme's own view (templates/collection.rfd-stock).
    // `limit` is set only where Shopify itself stops selling at zero — tracked
    // stock with "continue selling" off — so the import caps exactly where
    // checkout would, and nowhere else.
    async function stockMapFromView() {
      const map = {};
      const bust = Date.now();
      for (let page = 1; page < 20; page++) {
        const res = await fetch(`/collections/all?view=rfd-stock&page=${page}&_=${bust}`, { credentials: 'same-origin' });
        if (res.status === 429) { await sleep(1200); page--; continue; }
        if (!res.ok) throw new Error('stock view unavailable');
        const data = JSON.parse(await res.text());
        (data.items || []).forEach(v => {
          map[v.s] = {
            id: v.id,
            title: v.t || '',
            available: v.a !== false && !v.po,
            preorder: !!v.po,
            barcode: v.b || '',
            limit: v.m && v.d ? Math.max(0, Number(v.q) || 0) : null,
          };
        });
        if (!data.items || !data.items.length || page >= (data.pages || 1)) break;
        await sleep(150);
      }
      if (!Object.keys(map).length) throw new Error('stock view empty');
      return map;
    }

    // Fallback: yes/no availability only, no counts (so no partial capping).
    async function legacySkuMap() {
      const map = {};
      let page = 1;
      while (page < 50) { // safety cap
        const res = await getJson(`/products.json?limit=250&page=${page}`);
        if (!res.ok || !res.data) {
          if (page === 1) throw new Error('catalogue-load-failed');
          break; // partial catalogue on a later page — use what we have
        }
        const products = res.data.products || [];
        if (!products.length) break;
        products.forEach(p => {
          (p.variants || []).forEach(v => {
            if (v.sku) map[String(v.sku).toLowerCase()] = { id: v.id, available: v.available !== false, title: p.title || '', limit: null };
          });
        });
        if (products.length < 250) break;
        page++;
        await sleep(200); // be gentle across pages
      }
      return map;
    }

    // ── Confirmation recap ──────────────────────────────────────────────
    // Replaces the upload UI with a summary of exactly what was added and what
    // wasn't (with the reason). Each `lines` entry is { sku, qty, title }.
    function escHtml(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
    // A downloadable CSV of leftover lines, so a re-import adds ONLY those
    // (re-importing the full file would double-add what already went in).
    function remainingCsvHref(lines) {
      const body = 'SKU,QTY\n' + lines.map(l => `${l.sku.toUpperCase()},${l.qty}`).join('\n') + '\n';
      return 'data:text/csv;charset=utf-8,' + encodeURIComponent(body);
    }
    // The bounce-back: every line that did not go in in full, with what is
    // missing and why — something to send back, or to keep for the next order.
    function csvCell(v) {
      const t = String(v == null ? '' : v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    }
    function bounceBackRows({ outOfStock, partial, notFound, failed, notAttempted }) {
      const rows = [];
      // Multi-store lines name their store, so the note says which one is short.
      const at = (l) => (l.loc ? `Store ${l.loc} — ` : '');
      outOfStock.forEach(l => rows.push([l.sku.toUpperCase(), l.title, 'BO', l.qty,
        l.preorder
          ? `On pre-order — none added. Reserve it from the Pre-Orders tab of your account.`
          : `${at(l)}Out of stock — none added. Requested ${l.qty}, all ${l.qty} back-ordered.`]));
      partial.forEach(l => rows.push([l.sku.toUpperCase(), l.title, 'Partial stock', l.requested - l.qty,
        l.loc
          ? `${at(l)}Requested ${l.requested} — ${l.qty} added, ${l.requested - l.qty} back-ordered (not enough stock for every store; stores earlier in your file were served first).`
          : `Requested ${l.requested}, only ${l.qty} in stock — ${l.qty} added, ${l.requested - l.qty} back-ordered.`]));
      notFound.forEach(l => rows.push([l.sku.toUpperCase(), l.unknownStore ? (l.title || '') : '', 'Not found', l.qty,
        l.unknownStore
          ? `Store code ${l.loc} isn't one of your saved stores — none added. Requested ${l.qty}.`
          : `${at(l)}SKU not found in the catalogue — none added. Requested ${l.qty}.`]));
      failed.forEach(l => rows.push([l.sku.toUpperCase(), l.title, 'Not added', l.requested || l.qty,
        `Could not be added (${l.reason || 'unknown reason'}) — none added. Requested ${l.requested || l.qty}.`]));
      notAttempted.forEach(l => rows.push([l.sku.toUpperCase(), l.title, 'Not added', l.requested || l.qty,
        `Store was busy and the import stopped before this line — none added. Requested ${l.requested || l.qty}; re-import it.`]));
      return rows;
    }
    // One recap CSV per store, so each store can check its own delivery:
    // SKU, product title, quantity, barcode, location code.
    let lastStoreRecaps = [];
    function storeRecapCsv(store) {
      const head = ['SKU', 'Product title', 'Quantity', 'Barcode', 'Location code'];
      const rows = store.lines.map(l => [l.sku.toUpperCase(), l.title, l.qty, l.barcode, store.code]);
      return '\ufeff' + [head, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
    }
    function downloadStoreRecaps(stores) {
      const stamp = new Date().toISOString().slice(0, 10);
      // Spaced out: browsers drop back-to-back downloads triggered in one tick.
      stores.forEach((s, i) => setTimeout(() => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([storeRecapCsv(s)], { type: 'text/csv;charset=utf-8' }));
        a.download = `order-recap-${s.code}-${stamp}.csv`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
      }, i * 400));
    }
    resultEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-store-recaps]') && lastStoreRecaps.length) downloadStoreRecaps(lastStoreRecaps);
    });
    function bounceBackHref(rows) {
      const head = ['SKU', 'Title', 'Status', 'Quantity', 'Note'];
      const body = [head, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
      // BOM so Excel reads accents and dashes correctly.
      return 'data:text/csv;charset=utf-8,' + encodeURIComponent('\ufeff' + body);
    }
    function recapLines(lines) {
      return lines.map(l => {
        const name = escHtml(l.title || '');
        const reason = l.reason ? `<span class="rfd-import__ln-reason">${escHtml(l.reason)}</span>` : '';
        const qty = l.requested && l.requested !== l.qty ? `×${l.qty} of ${l.requested}` : `×${l.qty}`;
        const store = l.loc ? `<span class="rfd-import__ln-store">${escHtml(l.loc)}</span>` : '';
        return `<li><span class="rfd-import__ln-sku">${escHtml(l.sku.toUpperCase())}</span>${store}`
          + `<span class="rfd-import__ln-qty">${qty}</span>`
          + `<span class="rfd-import__ln-name">${name}</span>${reason}</li>`;
      }).join('');
    }
    function recapGroup(kind, label, lines) {
      if (!lines.length) return '';
      return `<details class="rfd-import__group rfd-import__group--${kind}" open>
        <summary>${label}</summary>
        <ul>${recapLines(lines)}</ul>
      </details>`;
    }
    function renderRecap({ added, already, outOfStock, notFound, failed, notAttempted, partial, stores, splitError }) {
      already = already || []; notAttempted = notAttempted || []; partial = partial || [];
      hideProgress();
      showRecapView();
      const inOrder = added.length + already.length;
      const problems = outOfStock.length + notFound.length + failed.length + notAttempted.length;
      const bounce = bounceBackRows({ outOfStock, partial, notFound, failed, notAttempted });
      const headText = added.length
        ? `${added.length} line${added.length === 1 ? '' : 's'} set in your cart`
        : already.length
          ? 'Your cart already matches your file'
          : 'Nothing could be added';
      let html = `<div class="rfd-import__recap-head ${inOrder > 0 ? 'is-ok' : 'is-bad'}">`
        + `<strong>${headText}</strong>`
        + (problems ? `<span class="rfd-import__recap-skip">${problems} not added</span>` : '')
        + (partial.length ? `<span class="rfd-import__recap-skip">${partial.length} partly added</span>` : '')
        + `</div>`;
      if (splitError) {
        html += `<div class="rfd-import__busy"><strong>Your items are in the cart, but the split per store wasn't saved</strong> (${escHtml(splitError)}). `
          + `Don't check out yet — import the file again, or contact us and we'll split it for you.</div>`;
      }
      if (stores && stores.length) {
        html += `<div class="rfd-import__stores"><strong>One order, shipped to ${stores.length} store${stores.length === 1 ? '' : 's'}:</strong> `
          + stores.map(s => `${escHtml(s.code)} (${s.units})`).join(' · ')
          + `<br><small>Check out once; each store gets its own delivery.</small></div>`;
        html += `<button type="button" class="rfd-import__bounce btn btn-outline btn-sm" data-store-recaps>`
          + `Download store recaps · ${stores.length} CSV${stores.length === 1 ? '' : 's'}</button>`;
        lastStoreRecaps = stores;
      }
      if (bounce.length) {
        const stamp = new Date().toISOString().slice(0, 10);
        html += `<a class="rfd-import__bounce btn btn-outline btn-sm" href="${bounceBackHref(bounce)}" download="bounce-back-${stamp}.csv">`
          + `Download bounce-back CSV · ${bounce.length} line${bounce.length === 1 ? '' : 's'}</a>`;
      }
      if (notAttempted.length) {
        html += `<div class="rfd-import__busy">The store got busy and stopped partway. `
          + `<strong>${added.length} set so far.</strong> Wait a minute and re-import the same file — items already in your cart are kept, only the rest get added (no double-ups). `
          + `<a href="${remainingCsvHref(notAttempted)}" download="remaining-items.csv">Download the remaining ${notAttempted.length} as CSV</a> to import just those.</div>`;
      }
      html += recapGroup('ok',   `Added / updated · ${added.length}`, added);
      html += recapGroup('info', `Already in your cart · ${already.length}`, already);
      html += recapGroup('warn', `Partial stock — only what's in stock was added · ${partial.length}`, partial);
      html += recapGroup('warn', `Out of stock / pre-order · ${outOfStock.length}`, outOfStock);
      html += recapGroup('bad',  `Couldn't be added · ${failed.length}`, failed);
      html += recapGroup('bad',  `Not added (store busy) · ${notAttempted.length}`, notAttempted);
      html += recapGroup('bad',  `SKU not found · ${notFound.length}`, notFound);
      resultEl.className = 'rfd-import__result rfd-import__recap';
      resultEl.style.display = 'block';
      resultEl.innerHTML = html;
      resultEl.scrollIntoView({ block: 'nearest' });
      submitBtn.disabled = false;
      if (inOrder > 0) { submitBtn.style.display = ''; submitBtn.textContent = 'View Cart →'; submitMode = 'viewcart'; }
      else { submitBtn.style.display = 'none'; }
    }

    let submitMode = 'submit'; // 'submit' | 'close' | 'viewcart'
    submitBtn.addEventListener('click', async () => {
      if (submitMode === 'close') { closeModal(); return; }
      if (submitMode === 'viewcart') { pendingDrawerOpen = true; closeModal(); return; }
      if (!parsedRows.length) return;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Looking up SKUs…';
      try {
        const skuMap = await buildSkuMap();
        // Read the current cart so we can SET each line to the file's qty (and
        // recognise what's already there) rather than blindly add on top.
        submitBtn.textContent = 'Reading your cart…';
        let cartNow = { items: [] };
        try { cartNow = await rfdFetchCart(); } catch (_) {}
        const curQty = new Map((cartNow.items || []).map(i => [String(i.variant_id), i.quantity]));

        if (parsedRows.some(r => r.loc)) {
          submitBtn.textContent = 'Checking your stores…';
          const res = await getJson('/apps/rfd-tools/multistore');
          const codes = ((res.data && res.data.locations) || []).map(l => String(l.code).toUpperCase());
          // A store file only if it names at least one of THEIR stores. Otherwise
          // it is an ordinary file with extra columns: read it as SKU + quantity,
          // exactly as before multi-store existed.
          if (codes.length && parsedRows.some(r => codes.includes(r.loc))) {
            await runMultiStore(skuMap, curQty, codes);
            return;
          }
          parsedRows = parsePlain(lastCsvText);
        }

        const toUpdate = [];     // matched, in stock, qty differs from cart → set
        const already = [];      // matched, in stock, already at the file qty
        const notFound = [];     // SKU not in catalogue
        const outOfStock = [];   // matched but out of stock / pre-order
        const partial = [];      // in stock, but fewer than the file asks for
        parsedRows.forEach(({ sku, qty }) => {
          const hit = skuMap[sku];
          if (!hit) { notFound.push({ sku, qty, title: '' }); return; }
          if (!hit.available || hit.limit === 0) { outOfStock.push({ sku, qty, title: hit.title, preorder: !!hit.preorder }); return; }
          // Set the line to what exists, not to what was asked: the cart endpoint
          // takes any quantity, and checkout would only stop them at the end.
          const take = hit.limit != null && qty > hit.limit ? hit.limit : qty;
          const line = { sku, qty: take, requested: qty, title: hit.title, id: hit.id };
          if (take < qty) partial.push(line);
          const cur = curQty.get(String(hit.id)) || 0;
          if (cur === take) { already.push(line); return; }
          toUpdate.push(line);
        });

        // Nothing to change (all already set, or all skipped) → recap only.
        if (!toUpdate.length) {
          renderRecap({ added: [], already, outOfStock, notFound, failed: [], partial });
          return;
        }

        submitBtn.textContent = 'Updating your cart…';
        const { added, failed, rateLimited } = await applyUpdates(toUpdate);
        // Lines we never attempted because the store throttled mid-run.
        const done = new Set(added.concat(failed));
        const notAttempted = rateLimited ? toUpdate.filter(l => !done.has(l)) : [];

        const cart = await rfdFetchCart();
        rfdUpdateCount(cart);
        rfdRenderCart(cart);
        rfdSyncCardsFromCart(cart);

        // A partial line that then failed outright is a failure, not a partial.
        const partialDone = partial.filter(l => !failed.includes(l) && !notAttempted.includes(l));
        renderRecap({ added, already, outOfStock, notFound, failed, notAttempted, partial: partialDone });
        if (added.length || already.length) pendingDrawerOpen = true;
      } catch (err) {
        hideProgress();
        resultEl.style.display = 'block';
        resultEl.className = 'rfd-import__result rfd-import__result--error';
        const detail = err && err.message ? String(err.message) : 'unknown error';
        resultEl.innerHTML = (detail === 'catalogue-load-failed')
          ? "<strong>Couldn't load the product catalogue.</strong><br><small>The store is busy right now — please wait a minute and try again.</small>"
          : `<strong>Something went wrong during the import.</strong><br><small>Please try again in a moment. If it keeps happening, send us the file and we'll place the order. <span style="opacity:.65">Details: ${escHtml(detail)}</span></small>`;
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add to Cart';
      }
    });

    // The multi-store import: plan per store, set the cart to the combined
    // quantities, save the split with the app, and tag the cart so checkout
    // carries it into the paid order (which the app then splits per store).
    async function runMultiStore(skuMap, curQty, codes) {
      const plan = planMultiStore(parsedRows, skuMap, curQty, codes);

      let added = [], failed = [], notAttempted = [];
      if (plan.toUpdate.length) {
        submitBtn.textContent = 'Updating your cart…';
        const r = await applyUpdates(plan.toUpdate);
        added = r.added; failed = r.failed;
        const done = new Set(added.concat(failed));
        notAttempted = r.rateLimited ? plan.toUpdate.filter(l => !done.has(l)) : [];
      }
      // Only what is actually in the cart is split.
      const inCart = new Set(added.concat(plan.already).map(l => l.sku));
      const allocation = Object.entries(plan.allocation)
        .map(([code, lines]) => ({ code, lines: lines.filter(l => inCart.has(l.sku)) }))
        .filter(s => s.lines.length);

      let stores = [];
      let splitError = '';
      if (allocation.length) {
        submitBtn.textContent = 'Saving the split…';
        const saved = await fetch('/apps/rfd-tools/multistore', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ allocation }),
        }).then(r => r.json()).catch(() => null);
        if (saved && saved.token) {
          await fetch('/cart/update.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { _rfd_multistore: saved.token, _rfd_multistore_stores: String(allocation.length) } }),
          }).catch(() => {});
          stores = allocation.map(s => ({
            code: s.code,
            units: s.lines.reduce((n, l) => n + l.qty, 0),
            lines: s.lines.map(l => ({ sku: l.sku, qty: l.qty, title: (skuMap[l.sku] || {}).title || '', barcode: (skuMap[l.sku] || {}).barcode || '' })),
          }));
        } else {
          splitError = (saved && saved.error) || 'The split per store could not be saved.';
        }
      }

      const cart = await rfdFetchCart();
      rfdUpdateCount(cart);
      rfdRenderCart(cart);
      rfdSyncCardsFromCart(cart);
      // A store's partial line only counts if its SKU actually went in.
      const partial = plan.partial.filter(l => inCart.has(l.sku));
      renderRecap({ added, already: plan.already, outOfStock: plan.outOfStock, notFound: plan.notFound, failed, notAttempted, partial, stores, splitError });
      if (added.length || plan.already.length) pendingDrawerOpen = true;
    }

    // Set cart quantities with as FEW requests as possible (rate-limit safe),
    // reporting progress. /cart/update.js is atomic — one bad line 422s the
    // whole batch — so we BISECT on 422: a whole 100-line order that's all valid
    // is ONE request; a line over its max-qty limit isolates itself while its
    // good half-batches still apply in bulk. Requests stay ~O(bad · log N), and
    // a healthy order of any size is a single request. A 429 (throttle) ABORTS —
    // never bisect a rate-limited store (that only makes it worse).
    async function applyUpdates(lines) {
      const added = [];   // successfully set/added (original line refs)
      const failed = [];  // couldn't set (original refs, with .reason attached)
      const total = lines.length;
      let rateLimited = false;
      setProgress(0, total, `Updating ${total} item${total === 1 ? '' : 's'} in your cart…`);

      const ABORT = {}; // thrown to unwind the recursion on throttle

      async function go(chunk) {
        if (!chunk.length) return;
        const updates = {};
        chunk.forEach(l => { updates[String(l.id)] = l.qty; });
        const res = await cartUpdate(updates);
        if (res.ok) {
          added.push(...chunk);
          setProgress(added.length + failed.length, total);
          return;
        }
        if (res.status === 429 || res.status === 0) throw ABORT;
        if (chunk.length === 1) {
          chunk[0].reason = addFailReason(res);
          failed.push(chunk[0]);
          setProgress(added.length + failed.length, total);
          return;
        }
        const mid = Math.floor(chunk.length / 2);
        await go(chunk.slice(0, mid));
        await go(chunk.slice(mid));
      }

      try {
        await go(lines);
      } catch (e) {
        if (e === ABORT) rateLimited = true;
        else throw e;
      }
      return { added, failed, rateLimited };
    }
  }

  /* ============================================================
     ACCOUNT v2 — tab switching + bulk reorder
     ============================================================ */
  function initAccountV2() {
    const nav = document.querySelector('.rfd-account-v2__nav');
    if (!nav) return;
    const tabs = nav.querySelectorAll('[data-acct-tab]');
    const panes = document.querySelectorAll('.rfd-account-v2__tab[data-acct-pane]');

    function show(name, scroll) {
      tabs.forEach(b => {
        const isActive = b.dataset.acctTab === name;
        b.classList.toggle('active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      panes.forEach(p => p.classList.toggle('is-active', p.dataset.acctPane === name));
      // On an explicit tab switch (click/jump), return to the top of the page —
      // otherwise switching from a control low on the page leaves you scrolled down.
      if (scroll) window.scrollTo({ top: 0, behavior: 'smooth' });
      // Sync hash for direct linking
      if (history.replaceState) {
        const h = name === 'dashboard' ? '' : '#' + name;
        history.replaceState({}, '', window.location.pathname + h);
      }
      // Refresh restock alerts on each tab open to pick up changes
      // made while the tab was inactive (e.g., a fresh subscribe from a product page).
      if (name === 'restock') { loadRestockAlerts(); loadNotifPrefs(); }
      if (name === 'preorders') { selectPreorderSubtab('open'); loadPreorders(); loadSecured(); }
      if (name === 'suggest-brand') { initBrandVote(); }
    }

    /* ── Suggest a Brand + voting ───────────────────────────────── */
    let brandVoteBound = false;
    let brandList = [];
    const bvTimers = {};
    const BV_STAR = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5.8 11.3 2 22l10.7-3.79"/><path d="M4 3h.01"/><path d="M22 8h.01"/><path d="M15 2h.01"/><path d="M22 20h.01"/><path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L12 10"/><path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17"/><path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7"/><path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z"/></svg>';
    function bvEsc(s){ const d = document.createElement('div'); d.textContent = String(s == null ? '' : s); return d.innerHTML; }
    function bvNorm(s){ return (s||'').toString().normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().replace(/[^a-z0-9]+/g,''); }
    function bvLev(a,b){ if(a===b)return 0; if(!a.length)return b.length; if(!b.length)return a.length; let p=Array.from({length:b.length+1},(_,i)=>i); for(let i=1;i<=a.length;i++){ const c=[i]; for(let j=1;j<=b.length;j++){ const cost=a[i-1]===b[j-1]?0:1; c[j]=Math.min(c[j-1]+1,p[j]+1,p[j-1]+cost);} p=c;} return p[b.length]; }
    function bvRatio(a,b){ const m=Math.max(a.length,b.length); return m?1-bvLev(a,b)/m:1; }

    function initBrandVote() {
      if (!brandVoteBound) { brandVoteBound = true; bindBrandVote(); }
      loadBrands();
    }

    async function loadBrands() {
      const wrap = document.querySelector('[data-brand-list]');
      if (!wrap) return;
      try {
        const r = await fetch('/apps/rfd-tools/brands/list', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        const data = await r.json();
        brandList = data.brands || [];
        // Sort ONCE on load by total cheers, then keep that order fixed for
        // the session so rows don't move while people cheer; the list
        // re-orders correctly on the next page load.
        brandList.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
      } catch (e) { brandList = []; }
      renderBrands();
    }

    function renderBrands() {
      const wrap = document.querySelector('[data-brand-list]');
      if (!wrap) return;
      // Hide the whole voting list until there are at least 2 approved brands.
      const listwrap = document.querySelector('[data-brand-listwrap]');
      if (listwrap) listwrap.style.display = brandList.length >= 2 ? '' : 'none';
      if (brandList.length < 2) return;
      const searchEl = document.querySelector('[data-brand-search]');
      const q = bvNorm(searchEl ? searchEl.value : '');
      let items = brandList;
      if (q) items = brandList.filter(b => { const k = bvNorm(b.name); return k.includes(q) || q.includes(k) || bvRatio(k,q) >= 0.7; });
      if (!items.length) { wrap.innerHTML = '<p class="rfd-notif-empty">' + (q ? 'No brands match your search.' : 'No brands yet — be the first to suggest one!') + '</p>'; return; }
      wrap.innerHTML = items.map(b => {
        const lvl = b.level || 0;
        const badge = b.label === 'coming_soon' ? '<span class="rfd-brandvote__badge rfd-brandvote__badge--soon">Coming soon</span>'
          : b.label === 'in_discussion' ? '<span class="rfd-brandvote__badge rfd-brandvote__badge--disc">In discussion</span>' : '';
        return '<div class="rfd-brandvote__row" data-brand-id="' + b.id + '">'
          + '<div class="rfd-brandvote__info"><span class="rfd-brandvote__name">' + bvEsc(b.name) + '</span>' + badge
          +   '<span class="rfd-brandvote__score" data-brand-score>' + b.score + ' total cheer' + (b.score === 1 ? '' : 's') + '</span></div>'
          + '<div class="rfd-brandvote__cheer">'
          +   '<span class="rfd-brandvote__hint"><span class="rfd-brandvote__hint-text">SHOW YOUR INTEREST<br>TAP TO CHEER</span><svg class="rfd-brandvote__hint-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></span>'
          +   '<button type="button" class="rfd-brandvote__cheerbtn' + (lvl ? ' is-on' : '') + '" data-cheer aria-pressed="' + (lvl ? 'true' : 'false') + '" aria-label="Cheer this brand">' + BV_STAR + '</button>'
          + '</div></div>';
      }).join('');
    }

    function bvUpdateRow(b) {
      const row = document.querySelector('[data-brand-id="' + b.id + '"]');
      if (!row) return;
      const lvl = b.level || 0;
      const btn = row.querySelector('[data-cheer]');
      const scoreEl = row.querySelector('[data-brand-score]');
      if (btn) {
        btn.classList.toggle('is-on', lvl > 0);
        btn.setAttribute('aria-pressed', lvl > 0 ? 'true' : 'false');
        btn.classList.remove('bvpop'); void btn.offsetWidth; btn.classList.add('bvpop');
      }
      if (scoreEl) scoreEl.textContent = b.score + ' total cheer' + (b.score === 1 ? '' : 's');
    }

    async function bvSaveVote(id) {
      const b = brandList.find(x => x.id === id);
      if (!b) return;
      try {
        await fetch('/apps/rfd-tools/brands/vote', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ brand_id: id, level: b.level }),
        });
        // No live re-sort: keep row order stable while cheering. The list
        // re-orders by score on the next page load (or for the next visitor).
      } catch (err) { if (window.rfdToast) window.rfdToast('Could not save your vote.'); }
    }

    function bindBrandVote() {
      const root = document.querySelector('[data-brands-root]');
      if (!root) return;
      const searchEl = root.querySelector('[data-brand-search]');
      if (searchEl) searchEl.addEventListener('input', renderBrands);
      root.addEventListener('click', (e) => {
        const row = e.target.closest('[data-brand-id]');
        if (!row) return;
        const isCheer = e.target.closest('[data-cheer]');
        if (!isCheer) return;
        const id = row.dataset.brandId;
        const b = brandList.find(x => x.id === id);
        if (!b) return;
        // Binary cheer: the star toggles on/off (max 1 per customer). A
        // legacy multi-level vote (old 2–5 scale) clears to 0 on first tap.
        // UI updates instantly; the save is debounced.
        const cur = b.level || 0;
        const next = cur >= 1 ? 0 : 1;
        if (next === cur) return;
        b.score += (next - cur);
        b.level = next;
        bvUpdateRow(b);
        clearTimeout(bvTimers[id]);
        bvTimers[id] = setTimeout(() => bvSaveVote(id), 550);
      });
      const submit = root.querySelector('[data-brand-submit]');
      if (submit) submit.addEventListener('click', submitBrandSuggestion);
    }

    async function submitBrandSuggestion() {
      const root = document.querySelector('[data-brands-root]');
      if (!root) return;
      const nameEl = root.querySelector('[data-brand-name]');
      const webEl = root.querySelector('[data-brand-website]');
      const noteEl = root.querySelector('[data-brand-note]');
      const msg = root.querySelector('[data-brand-msg]');
      const name = (nameEl && nameEl.value || '').trim();
      if (!name) { if (msg) msg.textContent = 'Enter a brand name.'; return; }
      try {
        const r = await fetch('/apps/rfd-tools/brands/suggest', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ brand_name: name, website: (webEl && webEl.value || '').trim(), note: (noteEl && noteEl.value || '').trim(), source: 'dashboard', customer_email: root.dataset.customerEmail || '', customer_name: root.dataset.customerName || '' }) });
        if (r.ok) {
          if (msg) msg.textContent = "Thanks! We'll review it and email you.";
          if (nameEl) nameEl.value = ''; if (webEl) webEl.value = ''; if (noteEl) noteEl.value = '';
          if (window.rfdToast) window.rfdToast('Brand suggested — thank you!');
        } else if (msg) { msg.textContent = 'Could not submit. Try again.'; }
      } catch (err) { if (msg) msg.textContent = 'Could not submit. Try again.'; }
    }

    /* With all marketing off, nothing in the brand grid sends — so it is shown
       as inactive (values kept for when it is switched back on), and the
       newsletter switch with it. `allOn` undefined = leave as it is. */
    function applyMarketingOverride(allOn) {
      if (allOn === undefined) return;
      const grid = document.querySelector('[data-notif-brands]');
      const wide = document.querySelector('[data-notif-wide]');
      if (grid) {
        grid.classList.toggle('is-overridden', !allOn);
        let note = grid.parentElement.querySelector('.rfd-notif-overridden-note');
        if (!allOn && !note) {
          note = document.createElement('p');
          note.className = 'rfd-notif-overridden-note';
          note.textContent = 'All marketing emails are off, so none of these are sent.';
          grid.insertAdjacentElement('afterend', note);
        } else if (allOn && note) {
          note.remove();
        }
      }
      // Everything above "All marketing" is governed by it.
      if (wide) {
        wide.querySelectorAll('[data-notif-wide-toggle]').forEach((cb) => {
          if (cb.dataset.notifWideToggle === 'all_marketing') return;
          cb.disabled = !allOn;
          const row = cb.closest('.rfd-notif-wide__row');
          if (row) row.style.opacity = allOn ? '' : '0.45';
        });
      }
    }

    /* New-products / pre-order-campaign per-brand toggles. Binds once; fetches
       the customer's saved overrides once (defaults render as ON via Liquid). */
    let notifBound = false;
    let notifLoaded = false;
    async function loadNotifPrefs() {
      const wrap = document.querySelector('[data-notif-brands]');
      if (!wrap) return;
      if (!notifBound) {
        notifBound = true;
        const savedEl = wrap.parentElement.querySelector('[data-notif-saved]');
        wrap.addEventListener('change', async (e) => {
          const cb = e.target.closest('[data-notif-toggle]');
          if (!cb) return;
          const row = cb.closest('[data-notif-brand]');
          const brandName = row ? (row.querySelector('.rfd-notif-brand-row__name') || {}).textContent || '' : '';
          try {
            const r = await fetch('/apps/rfd-tools/notifications/prefs', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                brand_handle: cb.dataset.brand,
                field: cb.dataset.notifToggle,
                value: cb.checked,
                brand_name: brandName.trim(),
                customer_email: wrap.dataset.customerEmail || '',
                customer_name: wrap.dataset.customerName || '',
              }),
            });
            if (!r.ok) throw new Error('save failed');
            if (savedEl) {
              savedEl.style.display = '';
              clearTimeout(savedEl._t);
              savedEl._t = setTimeout(() => { savedEl.style.display = 'none'; }, 1500);
            }
          } catch (_) {
            cb.checked = !cb.checked;
            if (window.rfdToast) window.rfdToast('Could not save that change. Try again.');
          }
        });

        // Newsletters + unsubscribe-from-all. Not about a brand, so they post a
        // field with no brand_handle.
        const wide = document.querySelector('[data-notif-wide]');
        if (wide) {
          const wideSaved = wide.querySelector('[data-notif-wide-saved]');
          wide.addEventListener('change', async (e) => {
            const cb = e.target.closest('[data-notif-wide-toggle]');
            if (!cb) return;
            const field = cb.dataset.notifWideToggle;
            applyMarketingOverride(field === 'all_marketing' ? cb.checked : undefined);
            try {
              const r = await fetch('/apps/rfd-tools/notifications/prefs', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                  field,
                  value: cb.checked,
                  customer_email: wrap.dataset.customerEmail || '',
                  customer_name: wrap.dataset.customerName || '',
                }),
              });
              if (!r.ok) throw new Error('save failed');
              if (wideSaved) {
                wideSaved.style.display = '';
                clearTimeout(wideSaved._t);
                wideSaved._t = setTimeout(() => { wideSaved.style.display = 'none'; }, 1500);
              }
            } catch (_) {
              cb.checked = !cb.checked;
              applyMarketingOverride(field === 'all_marketing' ? cb.checked : undefined);
              if (window.rfdToast) window.rfdToast('Could not save that change. Try again.');
            }
          });
        }
      }
      if (notifLoaded) return;
      const rowsEl = wrap.querySelector('[data-notif-rows]');
      try {
        const r = await fetch('/apps/rfd-tools/notifications/prefs', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (!r.ok) throw new Error('load failed');
        const data = await r.json();
        notifLoaded = true;
        // Saved customer-wide state. Absent (logged out, older app) leaves the
        // switches at their rendered default: on.
        const wide = document.querySelector('[data-notif-wide]');
        if (wide) {
          const nl = wide.querySelector('[data-notif-wide-toggle="newsletters"]');
          const nb = wide.querySelector('[data-notif-wide-toggle="new_brands"]');
          const all = wide.querySelector('[data-notif-wide-toggle="all_marketing"]');
          if (nl && typeof data.newsletters === 'boolean') nl.checked = data.newsletters;
          if (nb && typeof data.new_brands === 'boolean') nb.checked = data.new_brands;
          if (all && typeof data.all_marketing === 'boolean') all.checked = data.all_marketing;
          applyMarketingOverride(all ? all.checked : undefined);
        }
        const esc = (s) => String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
        const prefMap = {};
        (data.prefs || []).forEach((p) => { prefMap[p.brand_handle] = p; });
        const brands = data.brands || [];
        if (!rowsEl) return;
        if (!brands.length) {
          rowsEl.innerHTML = '<p class="rfd-notif-empty">No brands available yet.</p>';
          return;
        }
        rowsEl.innerHTML = brands.map((b) => {
          const p = prefMap[b.handle] || {};
          const preOn = p.preorder_campaigns !== false;
          const newOn = p.new_products !== false;
          const showOn = p.brand_showcases !== false;
          return `
            <div class="rfd-notif-brand-row" data-notif-brand="${esc(b.handle)}">
              <div class="rfd-notif-brand-row__name">${esc(b.name)}</div>
              <label class="rfd-notif-switch">
                <input type="checkbox" data-notif-toggle="preorder" data-brand="${esc(b.handle)}" ${preOn ? 'checked' : ''}>
                <span class="rfd-notif-switch__track" aria-hidden="true"></span>
              </label>
              <label class="rfd-notif-switch">
                <input type="checkbox" data-notif-toggle="new_products" data-brand="${esc(b.handle)}" ${newOn ? 'checked' : ''}>
                <span class="rfd-notif-switch__track" aria-hidden="true"></span>
              </label>
              <label class="rfd-notif-switch">
                <input type="checkbox" data-notif-toggle="brand_showcases" data-brand="${esc(b.handle)}" ${showOn ? 'checked' : ''}>
                <span class="rfd-notif-switch__track" aria-hidden="true"></span>
              </label>
            </div>`;
        }).join('');
      } catch (_) {
        if (rowsEl) rowsEl.innerHTML = '<p class="rfd-notif-empty">Couldn’t load brands. Reload to try again.</p>';
      }
    }

    /* Pre-Orders sub-tabs (Open / Reserved / Closed) */
    let preorderSubtab = 'open';
    function selectPreorderSubtab(which) {
      preorderSubtab = which;
      const tab = document.querySelector('[data-preorder-tab]');
      if (!tab) return;
      tab.querySelectorAll('[data-preorder-subtab]').forEach(b => {
        const on = b.dataset.preorderSubtab === which;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      tab.querySelectorAll('[data-preorder-subpane]').forEach(p => {
        p.style.display = p.dataset.preorderSubpane === which ? '' : 'none';
      });
    }
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-preorder-subtab]');
      if (!btn) return;
      const which = btn.dataset.preorderSubtab;
      selectPreorderSubtab(which);
      if (which === 'closed') loadSecured();
      else loadPreorders();
    });

    tabs.forEach(b => b.addEventListener('click', () => show(b.dataset.acctTab, true)));

    /* ── Restock Alerts tab ──
       Fetches the customer's active subscriptions from the app proxy and
       renders rows with mode-toggle + unsubscribe. Loaded lazily when the
       tab opens, refreshed on every open. */
    let restockLoading = false;
    async function loadRestockAlerts() {
      const tab = document.querySelector('[data-restock-tab]');
      if (!tab) return;
      if (restockLoading) return;
      restockLoading = true;

      const loadingEl   = tab.querySelector('[data-restock-loading]');
      const loadedEl    = tab.querySelector('[data-restock-loaded]');
      const errorEl     = tab.querySelector('[data-restock-error]');
      const listProduct = tab.querySelector('[data-restock-list-product]');
      const listBrand   = tab.querySelector('[data-restock-list-brand]');
      const emptyProduct= tab.querySelector('[data-restock-empty-product]');
      const emptyBrand  = tab.querySelector('[data-restock-empty-brand]');

      loadingEl.style.display = '';
      loadedEl.style.display  = 'none';
      errorEl.style.display   = 'none';

      try {
        const r = await fetch('/apps/rfd-tools/restock/list', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (!r.ok) throw new Error('list failed');
        const data = await r.json();
        const subs = (data && data.subscriptions) || [];

        const productSubs = subs.filter(s => s.scope === 'product');
        const brandSubs   = subs.filter(s => s.scope !== 'product');

        listProduct.innerHTML = productSubs.map(renderRestockRow).join('');
        listBrand.innerHTML   = brandSubs.map(renderRestockRow).join('');
        emptyProduct.style.display = productSubs.length ? 'none' : 'block';
        emptyBrand.style.display   = brandSubs.length ? 'none' : 'block';

        loadingEl.style.display = 'none';
        loadedEl.style.display  = '';

        // Update nav badge + dashboard tile count
        const countEl = document.querySelector('[data-restock-count]');
        if (countEl) countEl.textContent = subs.length;
        const tileVal = document.querySelector('[data-restock-tile-value]');
        if (tileVal) tileVal.textContent = subs.length;
      } catch (err) {
        loadingEl.style.display = 'none';
        errorEl.style.display = 'flex';
      } finally {
        restockLoading = false;
      }
    }

    function renderRestockRow(s) {
      const escape = (str) => String(str || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
      const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
      const isPerm = s.mode === 'permanent';
      const brandName = s.brand_name || s.brand_handle;
      const collectionUrl = '/collections/' + encodeURIComponent(s.brand_handle);

      // Product-scope alerts: single product, always one-time → no mode toggle.
      if (s.scope === 'product') {
        const title = s.product_title || s.product_handle;
        const productUrl = '/products/' + encodeURIComponent(s.product_handle || '');
        return `
        <div class="rfd-restock-row rfd-restock-row--product" data-restock-id="${escape(s.id)}" data-restock-mode="one_time">
          <a href="${escape(productUrl)}" class="rfd-restock-row__brand-info">
            <div class="rfd-restock-row__brand">
              <div class="rfd-restock-row__name">${escape(title)}</div>
              <div class="rfd-restock-row__meta">
                <span class="rfd-restock-row__tag">This product</span> · ${escape(brandName)} · Subscribed ${escape(fmt(s.created_at))}
              </div>
            </div>
          </a>

          <span class="rfd-restock-row__onetime">One-time</span>

          <button type="button"
            class="rfd-resource-row__link rfd-resource-row__link--filled-black rfd-restock-row__remove"
            data-restock-remove
            aria-label="Remove alert for ${escape(title)}">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Remove
          </button>
        </div>
      `;
      }

      return `
        <div class="rfd-restock-row" data-restock-id="${escape(s.id)}" data-restock-mode="${escape(s.mode)}">
          <a href="${escape(collectionUrl)}" class="rfd-restock-row__brand-info">
            <div class="rfd-restock-row__brand">
              <div class="rfd-restock-row__name">${escape(brandName)}</div>
              <div class="rfd-restock-row__meta">
                Subscribed ${escape(fmt(s.created_at))}${s.notified_at ? ' · Last alert ' + escape(fmt(s.notified_at)) : ''}
              </div>
            </div>
          </a>

          <button type="button"
            class="rfd-restock-row__mode-btn${!isPerm ? ' is-active' : ''}"
            data-restock-mode-btn="one_time"
            aria-pressed="${!isPerm}">One-time</button>

          <button type="button"
            class="rfd-restock-row__mode-btn${isPerm ? ' is-active' : ''}"
            data-restock-mode-btn="permanent"
            aria-pressed="${isPerm}">Permanent</button>

          <button type="button"
            class="rfd-resource-row__link rfd-resource-row__link--filled-black rfd-restock-row__remove"
            data-restock-remove
            aria-label="Unsubscribe from ${escape(brandName)}">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            Unsubscribe
          </button>
        </div>
      `;
    }

    // Delegated handlers for mode toggle, unsubscribe, retry
    document.addEventListener('click', async (e) => {
      const tab = document.querySelector('[data-restock-tab]');
      if (!tab || !tab.contains(e.target)) return;

      // Retry button
      if (e.target.closest('[data-restock-retry]')) {
        loadRestockAlerts();
        return;
      }

      // Mode toggle
      const modeBtn = e.target.closest('[data-restock-mode-btn]');
      if (modeBtn) {
        const row = modeBtn.closest('[data-restock-id]');
        if (!row || row.dataset.restockMode === modeBtn.dataset.restockModeBtn) return;
        const id = row.dataset.restockId;
        const mode = modeBtn.dataset.restockModeBtn;
        const prev = row.dataset.restockMode;

        // Optimistic UI
        row.dataset.restockMode = mode;
        row.querySelectorAll('[data-restock-mode-btn]').forEach(b => {
          const active = b.dataset.restockModeBtn === mode;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        try {
          const r = await fetch('/apps/rfd-tools/restock/mode', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id, mode }),
          });
          if (!r.ok) throw new Error('mode failed');
          if (window.rfdToast) window.rfdToast(mode === 'permanent' ? 'Switched to permanent alerts.' : 'Switched to one-time alert.');
        } catch (_) {
          // Revert on failure
          row.dataset.restockMode = prev;
          row.querySelectorAll('[data-restock-mode-btn]').forEach(b => {
            const active = b.dataset.restockModeBtn === prev;
            b.classList.toggle('is-active', active);
            b.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          if (window.rfdToast) window.rfdToast('Could not update. Try again.');
        }
        return;
      }

      // Unsubscribe
      const removeBtn = e.target.closest('[data-restock-remove]');
      if (removeBtn) {
        const row = removeBtn.closest('[data-restock-id]');
        if (!row) return;
        const id = row.dataset.restockId;
        removeBtn.disabled = true;
        row.style.opacity = '0.5';
        try {
          const r = await fetch('/apps/rfd-tools/restock/unsubscribe', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id }),
          });
          if (!r.ok) throw new Error('unsub failed');
          row.remove();
          // If list now empty, show empty state
          const listEl = tab.querySelector('[data-restock-list]');
          if (listEl && listEl.children.length === 0) {
            tab.querySelector('[data-restock-empty]').style.display = 'block';
          }
          // Update count badge + tile
          const remaining = listEl ? listEl.children.length : 0;
          const countEl = document.querySelector('[data-restock-count]');
          if (countEl) countEl.textContent = remaining;
          const tileVal = document.querySelector('[data-restock-tile-value]');
          if (tileVal) tileVal.textContent = remaining;
          if (window.rfdToast) window.rfdToast('Unsubscribed.');
        } catch (_) {
          removeBtn.disabled = false;
          row.style.opacity = '';
          if (window.rfdToast) window.rfdToast('Could not unsubscribe. Try again.');
        }
        return;
      }
    });

    // Load immediately on page load so the count badge + tile populate,
    // and so the list is already rendered when the user clicks the tab.
    if (document.querySelector('[data-restock-tab]')) {
      loadRestockAlerts();
    }

    /* ── Pre-Orders tab ──
       Fetches active campaigns + the customer's reservation quantities, groups
       by brand, renders expandable campaign cards with live-saving qty inputs. */
    let preorderLoading = false;
    let preorderData = null;             // last fetched campaigns array
    const PREORDER_SAVE_DEBOUNCE = 600;  // ms
    const saveTimers = {};               // campaignId -> timeout

    function fmtMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
    // Recompute each line total and the card's reservation total from the live
    // qty inputs (read-only cards fall back to their rendered data-qty).
    function recalcCardTotals(card) {
      let total = 0;
      card.querySelectorAll('.rfd-preorder-item').forEach(row => {
        const inp = row.querySelector('.rfd-preorder-qty');
        const price = parseInt(row.dataset.priceCents, 10) || 0;
        const qty = inp ? (parseInt(inp.value, 10) || 0) : (parseInt(row.dataset.qty, 10) || 0);
        total += price * qty;
        const lineEl = row.querySelector('[data-line-total]');
        if (lineEl) lineEl.textContent = qty > 0 ? `Line total: ${fmtMoney(price * qty)}` : '';
      });
      card.querySelectorAll('[data-preorder-total]').forEach(el => { el.textContent = fmtMoney(total); });
      return total;
    }
    function fmtDate(d) {
      if (!d) return '';
      const date = new Date(d);
      return isNaN(date) ? '' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    function fmtDateNum(d) {
      const date = new Date(d);
      if (!d || isNaN(date)) return '';
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return mm + '-' + dd + '-' + date.getFullYear();
    }
    function daysUntil(d) {
      return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
    }
    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    async function loadPreorders() {
      const tab = document.querySelector('[data-preorder-tab]');
      if (!tab) return;
      if (preorderLoading) return;
      preorderLoading = true;

      const loadingEl = tab.querySelector('[data-preorder-loading]');
      const emptyEl = tab.querySelector('[data-preorder-empty]');
      const listEl = tab.querySelector('[data-preorder-list]');
      const errorEl = tab.querySelector('[data-preorder-error]');

      loadingEl.style.display = '';
      emptyEl.style.display = 'none';
      errorEl.style.display = 'none';
      listEl.innerHTML = '';

      try {
        const r = await fetch('/apps/rfd-tools/preorder/campaigns', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (!r.ok) throw new Error('campaigns failed');
        const data = await r.json();
        const campaigns = (data && data.campaigns) || [];
        preorderData = campaigns;

        loadingEl.style.display = 'none';

        // Update dashboard tile + nav badge: count campaigns with a reservation
        renderOpenPane(campaigns);
        renderReservedPane(campaigns);
        if (campaigns.length) focusDeepLinkedCampaign();
        updatePreorderTile();
      } catch (err) {
        loadingEl.style.display = 'none';
        errorEl.style.display = 'flex';
        const resErr = tab.querySelector('[data-reserved-error]');
        if (resErr) resErr.style.display = 'flex';
      } finally {
        preorderLoading = false;
      }
    }

    // OPEN sub-pane = every campaign that's currently reservable, collapsed.
    function renderOpenPane(campaigns) {
      const tab = document.querySelector('[data-preorder-tab]');
      if (!tab) return;
      const listEl = tab.querySelector('[data-preorder-list]');
      const emptyEl = tab.querySelector('[data-preorder-empty]');
      if (!listEl) return;
      const has = !!(campaigns && campaigns.length);
      listEl.innerHTML = has ? renderPreorderGroups(campaigns, false) : '';
      if (emptyEl) emptyEl.style.display = has ? 'none' : 'block';
    }

    // RESERVED sub-pane = the open campaigns this customer has units in,
    // expanded by default so the reservation is visible at a glance.
    function renderReservedPane(campaigns) {
      const tab = document.querySelector('[data-preorder-tab]');
      if (!tab) return;
      const listEl = tab.querySelector('[data-reserved-list]');
      const emptyEl = tab.querySelector('[data-reserved-empty]');
      if (!listEl) return;
      const mine = (campaigns || []).filter(c => c.items.some(it => (it.quantity || 0) > 0));
      listEl.innerHTML = mine.length ? renderPreorderGroups(mine, true) : '';
      if (emptyEl) emptyEl.style.display = mine.length ? 'none' : 'block';
      const resErr = tab.querySelector('[data-reserved-error]');
      if (resErr) resErr.style.display = 'none';
      const navCount = document.querySelector('[data-reserved-count]');
      if (navCount) navCount.textContent = mine.length ? String(mine.length) : '';
    }

    // Deep-link from a RESERVE button: /account?preorder=<id>#preorders
    // Scroll to + briefly highlight the targeted campaign card.
    function focusDeepLinkedCampaign() {
      let target;
      try {
        target = new URLSearchParams(window.location.search).get('preorder');
      } catch (_) { target = null; }
      if (!target) return;
      const card = document.querySelector(`.rfd-preorder-card[data-campaign-id="${target}"]`);
      if (!card) return;
      // Ensure the pre-orders tab is visible first
      show('preorders');
      // Expand the targeted campaign accordion
      const toggle = card.querySelector('[data-preorder-toggle]');
      const body = card.querySelector('.rfd-preorder-card__body');
      card.classList.add('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (body) body.hidden = false;
      requestAnimationFrame(() => {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        card.classList.add('is-flash');
        setTimeout(() => card.classList.remove('is-flash'), 2000);
      });
    }

    // Dashboard "Active pre-orders" tile = total reserved products (sum of live
    // qty inputs). Nav badge = number of campaigns with at least one reserved unit.
    function updatePreorderTile() {
      let totalUnits = 0;
      const campaignsWithQty = new Set();
      document.querySelectorAll('[data-preorder-list] .rfd-preorder-card').forEach(card => {
        let cardUnits = 0;
        card.querySelectorAll('.rfd-preorder-qty').forEach(inp => {
          cardUnits += parseInt(inp.value, 10) || 0;
        });
        totalUnits += cardUnits;
        if (cardUnits > 0) campaignsWithQty.add(card.dataset.campaignId);
      });
      const tileVal = document.querySelector('[data-preorder-tile-value]');
      if (tileVal) tileVal.textContent = String(totalUnits);
      const navCount = document.querySelector('[data-preorder-count]');
      if (navCount) navCount.textContent = campaignsWithQty.size ? String(campaignsWithQty.size) : '';
    }

    // One flat list, closing soonest first — brand headings only split the list
    // into fragments that hid which campaign actually needed attention next.
    function renderPreorderGroups(campaigns, openByDefault) {
      return campaigns
        .slice()
        .sort((a, b) => new Date(a.cutoffAt).getTime() - new Date(b.cutoffAt).getTime())
        .map(c => renderPreorderCampaign(c, openByDefault))
        .join('');
    }

    // A campaign stops being news after this long. Past it the pill reads "Open"
    // and loses its animation — a container that has been taking reservations
    // for two months was still shouting NEW, which taught people to ignore it.
    const PREORDER_NEW_DAYS = 30;

    // Which pill a campaign in the open list gets.
    //   reserved → they have quantities in it; that outranks everything
    //   new      → open for less than a month
    //   open     → open longer than that
    // A campaign with no openedAt (older payloads, or a cached response from
    // before the field existed) is treated as not-new: quietly correct is better
    // than animating something months old.
    function preorderPillKind(c) {
      if (c.items && c.items.some(it => (it.quantity || 0) > 0)) return 'reserved';
      const opened = c.openedAt ? new Date(c.openedAt).getTime() : NaN;
      if (!opened || Number.isNaN(opened)) return 'open';
      return Date.now() - opened < PREORDER_NEW_DAYS * 86400000 ? 'new' : 'open';
    }

    // Standardised status pill, centred in every campaign header.
    function statusPill(kind) {
      const label =
        kind === 'reserved' ? 'Reserved' :
        kind === 'closed' ? 'Closed' :
        kind === 'open' ? 'Open' : 'New';
      return `<span class="rfd-preorder-status rfd-preorder-status--${kind}">${label}</span>`;
    }

    // MSRP + discount + price, mirroring snippets/product-list-row.liquid so the
    // account page and the collection list read identically.
    function priceBlock(priceCents, compareAtCents) {
      const msrp = compareAtCents || 0;
      const hasMsrp = msrp > priceCents && priceCents > 0;
      const pct = hasMsrp ? Math.round(100 - (priceCents * 100) / msrp) : 0;
      return `<div class="rfd-list-row__price-line">
          ${hasMsrp ? `<div class="rfd-list-row__msrp-block">
            <div class="rfd-list-row__msrp-label">MSRP</div>
            <div class="rfd-list-row__msrp-strike">${fmtMoney(msrp)}</div>
          </div>` : ''}
          ${hasMsrp && pct > 0 ? `<span class="rfd-list-row__discount">-${pct}%</span>` : ''}
          <div class="rfd-list-row__price-num">${fmtMoney(priceCents)}</div>
        </div>`;
    }

    function renderPreorderCampaign(c, openByDefault) {
      const dleft = daysUntil(c.cutoffAt);
      const closingSoon = dleft < 30;
      const cutoffLabel = dleft > 0
        ? `${dleft} day${dleft === 1 ? '' : 's'} left`
        : 'Closing today';
      const reservedTotal = c.reservation ? c.reservation.totalCents : 0;
      const itemsRows = c.items.map(it => {
        const productUrl = it.productHandle ? '/products/' + encodeURIComponent(it.productHandle) : '';
        const mediaInner = it.imageUrl ? `<img src="${esc(it.imageUrl)}" alt="" loading="lazy">` : '<div class="rfd-preorder-item__noimg"></div>';
        const media = productUrl
          ? `<a href="${esc(productUrl)}" target="_blank" rel="noopener" class="rfd-preorder-item__media">${mediaInner}</a>`
          : `<div class="rfd-preorder-item__media">${mediaInner}</div>`;
        const titleText = `${esc(it.productTitle)}${it.variantTitle ? ` — ${esc(it.variantTitle)}` : ''}`;
        const title = productUrl
          ? `<a href="${esc(productUrl)}" target="_blank" rel="noopener" class="rfd-preorder-item__title">${titleText}</a>`
          : `<span class="rfd-preorder-item__title">${titleText}</span>`;
        const inc = Math.max(1, it.increment || 1);
        const effMin = Math.ceil(Math.max(1, it.minQty || 1, inc) / inc) * inc;
        const ruleNote = inc > 1
          ? `<div class="rfd-preorder-item__rule">Increments of ${inc}</div>`
          : (effMin > 1 ? `<div class="rfd-preorder-item__rule">Minimum ${effMin}</div>` : '');
        const qty0 = it.quantity || 0;
        return `<div class="rfd-preorder-item" data-variant-id="${esc(it.variantId)}" data-price-cents="${it.priceCents}">
            ${media}
            <div class="rfd-preorder-item__info">
              ${title}
              ${it.sku ? `<div class="rfd-preorder-item__meta">SKU ${esc(it.sku)}</div>` : ''}
            </div>
            <div class="rfd-preorder-item__price">
              ${priceBlock(it.priceCents, it.compareAtCents)}
            </div>
            <div class="rfd-preorder-item__qty">
              ${ruleNote}
              <div class="rfd-list-stepper__ctrl rfd-preorder-stepper">
                <button type="button" class="rfd-list-stepper__btn" data-preorder-step="dec" aria-label="Decrease quantity">−</button>
                <input type="number" min="0" step="${inc}" inputmode="numeric"
                       class="rfd-preorder-qty rfd-list-stepper__val"
                       value="${it.quantity || 0}"
                       data-variant-id="${esc(it.variantId)}"
                       data-min-qty="${effMin}"
                       data-increment="${inc}"
                       aria-label="Quantity for ${esc(it.productTitle)}">
                <button type="button" class="rfd-list-stepper__btn" data-preorder-step="inc" aria-label="Increase quantity">+</button>
              </div>
              <div class="rfd-preorder-item__linetotal" data-line-total>${qty0 > 0 ? `Line total: ${fmtMoney(it.priceCents * qty0)}` : ''}</div>
            </div>
          </div>`;
      }).join('');

      const itemCount = c.items.length;
      const isOpen = !!openByDefault;
      return `<div class="rfd-preorder-card${isOpen ? ' is-open' : ''}" data-campaign-id="${esc(c.id)}">
          <button type="button" class="rfd-preorder-card__head" data-preorder-toggle aria-expanded="${isOpen ? 'true' : 'false'}">
            <div class="rfd-preorder-card__head-main">
              <div class="rfd-preorder-card__name">${esc(c.name)}</div>
              <div class="rfd-preorder-card__summary">
                ${itemCount} product${itemCount === 1 ? '' : 's'} · Reserved <strong data-preorder-total>${fmtMoney(reservedTotal)}</strong>
              </div>
            </div>
            ${statusPill(preorderPillKind(c))}
            <div class="rfd-preorder-card__head-end">
              <div class="rfd-preorder-card__head-right">
              <div class="rfd-preorder-card__statgroup">
                <span class="rfd-preorder-card__datestat">CLOSES ON: ${fmtDateNum(c.cutoffAt)}</span>
                <span class="rfd-preorder-card__cutoff ${closingSoon ? 'is-soon' : ''}">${esc(cutoffLabel)}</span>
              </div>
              ${c.etaAt ? `<span class="rfd-preorder-card__eta">EST. ARRIVAL: ${fmtDateNum(c.etaAt)}</span>` : ''}
              </div>
              <svg class="rfd-preorder-card__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </button>
          <div class="rfd-preorder-card__body"${isOpen ? '' : ' hidden'}>
            ${c.description ? `<div class="rfd-preorder-card__desc">${esc(c.description)}</div>` : ''}
            <div class="rfd-preorder-card__items">
              ${itemsRows}
            </div>
            <div class="rfd-preorder-card__total">
              <span class="rfd-preorder-card__total-label">Reservation total</span>
              <span class="rfd-preorder-card__total-value" data-preorder-total>${fmtMoney(reservedTotal)}</span>
            </div>
            <div class="rfd-preorder-card__foot">
              <span class="rfd-preorder-card__saving" data-preorder-saving></span>
              <button type="button" class="rfd-preorder-card__cancel" data-preorder-cancel
                      style="${c.reservation ? '' : 'display:none;'}">Cancel reservation</button>
            </div>
          </div>
        </div>`;
    }

    // Snap a qty to its rule: 0 stays 0 (removal); else nearest multiple of
    // increment, floored at the effective minimum.
    function snapQty(inp) {
      const q = parseInt(inp.value, 10) || 0;
      if (q <= 0) return 0;
      const inc = Math.max(1, parseInt(inp.dataset.increment, 10) || 1);
      const effMin = Math.max(1, parseInt(inp.dataset.minQty, 10) || 1);
      const snapped = Math.round(q / inc) * inc;
      return Math.max(effMin, snapped);
    }

    function collectLines(card) {
      const lines = [];
      card.querySelectorAll('.rfd-preorder-qty').forEach(inp => {
        lines.push({ variant_id: inp.dataset.variantId, quantity: snapQty(inp) });
      });
      return lines;
    }

    // Correct the visible input to the snapped value on blur/change.
    document.addEventListener('change', (e) => {
      const inp = e.target.closest('.rfd-preorder-qty');
      if (!inp) return;
      inp.value = String(snapQty(inp));
    });

    async function savePreorder(card) {
      const campaignId = card.dataset.campaignId;
      const savingEl = card.querySelector('[data-preorder-saving]');
      const totalEls = card.querySelectorAll('[data-preorder-total]');
      const cancelBtn = card.querySelector('[data-preorder-cancel]');
      if (savingEl) { savingEl.textContent = 'Saving…'; savingEl.className = 'rfd-preorder-card__saving is-saving'; }

      try {
        const tab = document.querySelector('[data-preorder-tab]');
        const r = await fetch('/apps/rfd-tools/preorder/reserve', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: campaignId,
            lines: collectLines(card),
            email: tab ? tab.dataset.customerEmail : '',
            name: tab ? tab.dataset.customerName : '',
            company: tab ? tab.dataset.customerCompany : '',
          }),
        });
        const data = await r.json();
        if (!r.ok || !data.ok) throw new Error(data.error || 'save failed');

        totalEls.forEach(el => { el.textContent = fmtMoney(data.totalCents || 0); });
        if (cancelBtn) cancelBtn.style.display = data.removed ? 'none' : '';
        if (savingEl) { savingEl.textContent = 'Saved'; savingEl.className = 'rfd-preorder-card__saving is-saved'; }
        setTimeout(() => { if (savingEl) savingEl.textContent = ''; }, 1500);

        // refresh dashboard tile + nav badge from live inputs
        if (preorderData) {
          const c = preorderData.find(x => x.id === campaignId);
          if (c) {
            c.reservation = data.removed ? null : { totalCents: data.totalCents };
            const byVariant = {};
            collectLines(card).forEach(l => { byVariant[l.variant_id] = l.quantity; });
            c.items.forEach(it => {
              if (byVariant[it.variantId] != null) it.quantity = byVariant[it.variantId];
            });
          }
        }
        updatePreorderTile();
        // The same campaign renders in both OPEN and RESERVED. Re-render
        // whichever pane isn't on screen so its contents (and the RESERVED
        // membership) stay in step with this edit.
        if (preorderSubtab === 'reserved') renderOpenPane(preorderData);
        else renderReservedPane(preorderData);
      } catch (err) {
        if (savingEl) { savingEl.textContent = (err && err.message) || 'Error — try again'; savingEl.className = 'rfd-preorder-card__saving is-error'; }
        if (window.rfdToast) window.rfdToast('Could not save pre-order. Try again.');
      }
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-preorder-step]');
      if (!btn) return;
      const inp = btn.parentElement && btn.parentElement.querySelector('.rfd-preorder-qty');
      if (!inp) return;
      const inc = Math.max(1, parseInt(inp.dataset.increment, 10) || 1);
      const effMin = Math.max(1, parseInt(inp.dataset.minQty, 10) || 1);
      const cur = parseInt(inp.value, 10) || 0;
      let next;
      if (btn.dataset.preorderStep === 'inc') {
        next = cur <= 0 ? effMin : cur + inc;
      } else {
        next = cur - inc;
        if (next < effMin) next = 0; // stepping under the minimum drops the line
      }
      inp.value = String(Math.max(0, next));
      // Same path as typing: recalc totals, then the debounced save.
      inp.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Debounced live-save on qty change (delegated)
    document.addEventListener('input', (e) => {
      const inp = e.target.closest('.rfd-preorder-qty');
      if (!inp) return;
      const card = inp.closest('.rfd-preorder-card');
      if (!card) return;
      recalcCardTotals(card);
      const id = card.dataset.campaignId;
      clearTimeout(saveTimers[id]);
      saveTimers[id] = setTimeout(() => savePreorder(card), PREORDER_SAVE_DEBOUNCE);
    });

    /* ── Secured Pre-Orders tab (read-only, post-cutoff) ── */
    let securedLoading = false;
    async function loadSecured() {
      const tab = document.querySelector('[data-secured-tab]');
      if (!tab) return;
      if (securedLoading) return;
      securedLoading = true;
      const loadingEl = tab.querySelector('[data-secured-loading]');
      const emptyEl = tab.querySelector('[data-secured-empty]');
      const listEl = tab.querySelector('[data-secured-list]');
      const errorEl = tab.querySelector('[data-secured-error]');
      loadingEl.style.display = '';
      emptyEl.style.display = 'none';
      errorEl.style.display = 'none';
      listEl.innerHTML = '';
      try {
        const r = await fetch('/apps/rfd-tools/preorder/secured', {
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' },
        });
        if (!r.ok) throw new Error('secured failed');
        const data = await r.json();
        const closed = (data && data.campaigns) || [];
        loadingEl.style.display = 'none';
        // The badge counts the customer's own reservations, not the store-wide
        // list — that's the number that means something to them.
        const navCount = document.querySelector('[data-secured-count]');
        const mine = closed.filter(c => !!c.reservationStatus).length;
        if (navCount) navCount.textContent = mine ? String(mine) : '';
        if (closed.length === 0) {
          emptyEl.style.display = 'block';
        } else {
          listEl.innerHTML = closed
            .slice()
            .sort((a, b) => new Date(b.cutoffAt).getTime() - new Date(a.cutoffAt).getTime())
            .map(renderSecuredCard)
            .join('');
          focusMergeCard();
        }
      } catch (err) {
        loadingEl.style.display = 'none';
        errorEl.style.display = 'flex';
      } finally {
        securedLoading = false;
      }
    }

    // Stock arrived while they have an open order: add it to that order, or ship
    // it on its own. The email's buttons land here (?merge=<id>&choice=…); the
    // choice is only made by clicking, never by opening the link.
    function renderMergeChoice(m) {
      const deadline = m.deadline ? new Date(m.deadline).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : '';
      return `<div class="rfd-merge" data-merge-id="${esc(m.reservationId)}">
          <div class="rfd-merge__lead">Your pre-order has arrived and is set aside for you. You also have order <strong>${esc(m.orderName)}</strong> that hasn't shipped yet — how would you like to receive it?</div>
          <div class="rfd-merge__opts">
            <div class="rfd-merge__opt" data-merge-opt="add">
              <button type="button" class="btn btn-primary btn-sm" data-merge-choice="add">Add to ${esc(m.orderName)}</button>
              <p>One parcel. We add these items to ${esc(m.orderName)} and invoice only the difference. ${esc(m.orderName)} ships once that's paid.</p>
            </div>
            <div class="rfd-merge__opt" data-merge-opt="separate">
              <button type="button" class="btn btn-outline btn-sm" data-merge-choice="separate">Ship separately</button>
              <p>Its own order and invoice. ${esc(m.orderName)} ships as planned.${m.underMinimum ? ` On its own it's under our ${fmtMoney(m.minimumCents)} minimum, so it would need topping up before it can ship.` : ''}</p>
            </div>
          </div>
          ${deadline ? `<div class="rfd-merge__foot">No answer by ${esc(deadline)}? We'll ship it separately.</div>` : ''}
        </div>`;
    }

    function focusMergeCard() {
      let id = null;
      let choice = null;
      try {
        const q = new URLSearchParams(window.location.search);
        id = q.get('merge');
        choice = q.get('choice');
      } catch (_) {}
      if (!id) return;
      const panel = document.querySelector(`.rfd-merge[data-merge-id="${CSS.escape(id)}"]`);
      if (!panel) return;
      selectPreorderSubtab('closed');
      const card = panel.closest('.rfd-preorder-card');
      if (card) setCardOpen(card, true);
      if (choice === 'add' || choice === 'separate') {
        const opt = panel.querySelector(`[data-merge-opt="${choice}"]`);
        if (opt) opt.classList.add('is-suggested');
      }
      requestAnimationFrame(() => {
        (card || panel).scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (card) {
          card.classList.add('is-flash');
          setTimeout(() => card.classList.remove('is-flash'), 2000);
        }
      });
    }

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-merge-choice]');
      if (!btn) return;
      const panel = btn.closest('.rfd-merge');
      if (!panel || panel.dataset.busy) return;
      panel.dataset.busy = '1';
      const label = btn.textContent;
      btn.textContent = btn.dataset.mergeChoice === 'add' ? 'Adding…' : 'Setting up…';
      panel.querySelectorAll('[data-merge-choice]').forEach(b => { b.disabled = true; });
      try {
        const r = await fetch('/apps/rfd-tools/preorder/merge', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ reservationId: panel.dataset.mergeId, choice: btn.dataset.mergeChoice }),
        });
        const res = await r.json().catch(() => null);
        if (!res || !res.ok) {
          window.rfdToast((res && res.error) || 'Could not save your choice. Please try again.');
          return;
        }
        if (res.choice === 'add') {
          window.rfdToast(`Added to ${res.orderName}` + (res.outstandingCents > 0 ? ` — ${fmtMoney(res.outstandingCents)} now due. Invoice on its way.` : '.'));
        } else {
          window.rfdToast(res.fellBack || `Your pre-order ships separately${res.orderName ? ' as ' + res.orderName : ''}.`);
        }
        try { history.replaceState({}, '', window.location.pathname + '#preorders'); } catch (_) {}
        loadSecured();
      } catch (_) {
        window.rfdToast('Could not save your choice. Please try again.');
      } finally {
        panel.dataset.busy = '';
        btn.textContent = label;
        panel.querySelectorAll('[data-merge-choice]').forEach(b => { b.disabled = false; });
      }
    });

    function renderSecuredCard(s) {
      const hasRes = !!s.reservationStatus;
      const isInvoiced = s.reservationStatus === 'converted';
      const isChoice = s.reservationStatus === 'awaiting_choice' && !!s.merge;
      const statusLabel = !hasRes
        ? 'Closed'
        : (isChoice ? 'Arrived — your choice'
          : (isInvoiced ? (s.mergedInto ? 'Added to ' + s.mergedInto : 'Invoiced — awaiting payment')
            : (s.reservationStatus === 'fulfilled' ? 'Fulfilled' : 'Reserved — in transit')));
      const statusClass = !hasRes ? 'is-past' : (isChoice ? 'is-choice' : (isInvoiced ? 'is-invoiced' : 'is-locked'));
      const itemsRows = s.lines.map(it => {
        const productUrl = it.productHandle ? '/products/' + encodeURIComponent(it.productHandle) : '';
        const mediaInner = it.imageUrl ? `<img src="${esc(it.imageUrl)}" alt="" loading="lazy">` : '<div class="rfd-preorder-item__noimg"></div>';
        const media = productUrl
          ? `<a href="${esc(productUrl)}" target="_blank" rel="noopener" class="rfd-preorder-item__media">${mediaInner}</a>`
          : `<div class="rfd-preorder-item__media">${mediaInner}</div>`;
        const titleText = `${esc(it.productTitle)}${it.variantTitle ? ` — ${esc(it.variantTitle)}` : ''}`;
        const title = productUrl
          ? `<a href="${esc(productUrl)}" target="_blank" rel="noopener" class="rfd-preorder-item__title">${titleText}</a>`
          : `<span class="rfd-preorder-item__title">${titleText}</span>`;
        return `<div class="rfd-preorder-item" data-price-cents="${it.priceCents}" data-qty="${it.quantity}">
            ${media}
            <div class="rfd-preorder-item__info">
              ${title}
              ${it.sku ? `<div class="rfd-preorder-item__meta">SKU ${esc(it.sku)}</div>` : ''}
            </div>
            <div class="rfd-preorder-item__price">
              ${priceBlock(it.priceCents, it.compareAtCents)}
            </div>
            <div class="rfd-preorder-item__qty">
              <div class="rfd-secured-item__qty">${it.quantity > 0 ? `×${it.quantity}` : ''}</div>
              ${it.quantity > 0 ? `<div class="rfd-preorder-item__linetotal">Line total: ${fmtMoney(it.priceCents * it.quantity)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      const itemCount = s.lines.length;
      return `<div class="rfd-preorder-card rfd-secured-card${hasRes ? '' : ' is-unreserved'}">
          <button type="button" class="rfd-preorder-card__head" data-preorder-toggle aria-expanded="false">
            <div class="rfd-preorder-card__head-main">
              <div class="rfd-preorder-card__name">${esc(s.campaignName)}</div>
              <div class="rfd-preorder-card__summary">
                ${esc(s.brandName)} · ${itemCount} product${itemCount === 1 ? '' : 's'}${hasRes ? ` · Reserved <strong>${fmtMoney(s.totalCents)}</strong>` : ''}
              </div>
            </div>
            ${statusPill('closed')}
            <div class="rfd-preorder-card__head-end">
              <div class="rfd-preorder-card__head-right">
              <div class="rfd-preorder-card__statgroup">
                <span class="rfd-preorder-card__datestat">DATE CLOSED: ${fmtDateNum(s.cutoffAt)}</span>
                <span class="rfd-secured-status ${statusClass}">${esc(statusLabel)}</span>
              </div>
              ${s.etaAt ? `<span class="rfd-preorder-card__eta">EST. ARRIVAL: ${fmtDateNum(s.etaAt)}</span>` : ''}
              </div>
              <svg class="rfd-preorder-card__chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </button>
          <div class="rfd-preorder-card__body" hidden>
            ${isChoice ? renderMergeChoice(s.merge) : ''}
            ${s.description ? `<div class="rfd-preorder-card__desc">${esc(s.description)}</div>` : ''}
            <div class="rfd-preorder-card__items">${itemsRows}</div>
            ${hasRes ? `<div class="rfd-preorder-card__total">
              <span class="rfd-preorder-card__total-label">Reservation total</span>
              <span class="rfd-preorder-card__total-value">${fmtMoney(s.totalCents)}</span>
            </div>` : ''}
            ${isInvoiced && s.invoiceUrl ? `<div class="rfd-preorder-card__foot"><span></span><a href="${esc(s.invoiceUrl)}" target="_blank" rel="noopener" class="btn btn-primary btn-sm">Pay invoice →</a></div>` : ''}
          </div>
        </div>`;
    }

    // Accordion toggle (delegated)
    function setCardOpen(card, open) {
      const toggle = card.querySelector('[data-preorder-toggle]');
      const body = card.querySelector('.rfd-preorder-card__body');
      card.classList.toggle('is-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (body) body.hidden = !open;
    }
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-preorder-toggle]');
      if (!toggle) return;
      const card = toggle.closest('.rfd-preorder-card');
      if (!card) return;
      setCardOpen(card, !card.classList.contains('is-open'));
    });

    // Cancel reservation + retry (delegated)
    document.addEventListener('click', async (e) => {
      const retry = e.target.closest('[data-preorder-retry]');
      if (retry) { loadPreorders(); return; }
      const securedRetry = e.target.closest('[data-secured-retry]');
      if (securedRetry) { loadSecured(); return; }

      const cancelBtn = e.target.closest('[data-preorder-cancel]');
      if (!cancelBtn) return;
      const card = cancelBtn.closest('.rfd-preorder-card');
      if (!card) return;
      if (!window.confirm('Cancel this reservation? This clears all quantities for this pre-order.')) return;
      const campaignId = card.dataset.campaignId;
      cancelBtn.disabled = true;
      try {
        const r = await fetch('/apps/rfd-tools/preorder/cancel', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaign_id: campaignId }),
        });
        const data = await r.json();
        if (!r.ok || !data.ok) throw new Error(data.error || 'cancel failed');
        // Zero all inputs + reset total
        card.querySelectorAll('.rfd-preorder-qty').forEach(i => { i.value = 0; });
        card.querySelectorAll('[data-preorder-total]').forEach(el => { el.textContent = fmtMoney(0); });
        cancelBtn.style.display = 'none';
        if (window.rfdToast) window.rfdToast('Reservation cancelled.');
        if (preorderData) {
          const c = preorderData.find(x => x.id === campaignId);
          if (c) c.reservation = null;
        }
        updatePreorderTile();
      } catch (err) {
        if (window.rfdToast) window.rfdToast('Could not cancel. Try again.');
      } finally {
        cancelBtn.disabled = false;
      }
    });

    // Eager-load so the dashboard pre-order tile count populates on page load.
    if (document.querySelector('[data-preorder-tab]')) {
      loadPreorders();
    }
    // Eager-load secured so its nav badge populates too.
    if (document.querySelector('[data-secured-tab]')) {
      loadSecured();
    }

    /* Row/card click on order tiles — navigate to order, but not when clicking interactive children */
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-order-href]');
      if (!target) return;
      if (e.target.closest('a, button, [data-no-row-nav]')) return;
      const newTab = e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1;
      if (newTab) window.open(target.dataset.orderHref, '_blank', 'noopener');
      else window.location.assign(target.dataset.orderHref);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const target = e.target.closest('[data-order-href]');
      if (!target) return;
      window.location.assign(target.dataset.orderHref);
    });

    /* Dashboard card click — jump to a tab */
    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-acct-jump]');
      if (!card) return;
      if (e.target.closest('a, button, [data-no-row-nav]')) return;
      show(card.dataset.acctJump, true);
      // Scroll the main panel into view in case the dashboard was scrolled
      const target = document.querySelector('.rfd-account-v2__main');
      if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const card = e.target.closest('[data-acct-jump]');
      if (!card) return;
      show(card.dataset.acctJump, true);
    });

    // Open from URL hash on first load
    const initialHash = window.location.hash.replace('#', '');
    if (initialHash && document.querySelector('[data-acct-pane="' + initialHash + '"]')) {
      show(initialHash);
    }

    // Reorder buttons → bulk add to cart
    const dataEl = document.getElementById('rfd-reorder-data');
    let reorderMap = {};
    if (dataEl) {
      try { reorderMap = JSON.parse(dataEl.textContent || '{}'); } catch (_) { reorderMap = {}; }
    }
    // Order detail page reorder — items already JSON-encoded in data-reorder-items
    document.addEventListener('click', async (e) => {
      const detailBtn = e.target.closest('[data-reorder-items]');
      if (!detailBtn) return;
      let items = [];
      try { items = JSON.parse(detailBtn.dataset.reorderItems || '[]'); } catch (_) {}
      items = items.filter(i => i && i.id && i.quantity > 0);
      if (!items.length) {
        window.rfdToast('No items to reorder.');
        return;
      }
      const orig = detailBtn.textContent;
      detailBtn.disabled = true;
      detailBtn.textContent = 'Adding…';
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!r.ok) throw new Error('reorder failed');
        const cart = await rfdFetchCart();
        rfdUpdateCount(cart);
        rfdRenderCart(cart);
        if (typeof rfdSyncCardsFromCart === 'function') rfdSyncCardsFromCart(cart);
        window.rfdToast(`Reordered ${items.length} item${items.length === 1 ? '' : 's'}.`);
        rfdOpenDrawer();
      } catch (err) {
        window.rfdToast('Could not reorder. Try again.');
      } finally {
        detailBtn.disabled = false;
        detailBtn.textContent = orig;
      }
    });

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-reorder-order]');
      if (!btn) return;
      const orderId = btn.dataset.reorderOrder;
      const items = (reorderMap[orderId] || []).filter(i => i.id && i.id !== 0 && i.quantity > 0);
      if (!items.length) {
        window.rfdToast('No items to reorder.');
        return;
      }
      const orig = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!r.ok) throw new Error('reorder failed');
        const cart = await rfdFetchCart();
        rfdUpdateCount(cart);
        rfdRenderCart(cart);
        if (typeof rfdSyncCardsFromCart === 'function') rfdSyncCardsFromCart(cart);
        window.rfdToast(`Reordered ${items.length} item${items.length === 1 ? '' : 's'}.`);
        rfdOpenDrawer();
      } catch (err) {
        window.rfdToast('Could not reorder. Try again.');
      } finally {
        btn.disabled = false;
        btn.textContent = orig;
      }
    });
  }

  /* ============================================================
     RESTOCK ALERTS (brand-level) — UI shell
     Backend is the rfd-selling-tools app (POST /apps/rfd-tools/restock/subscribe).
     Until the app ships, submits show a toast and close. The plumbing is
     ready — when the app is live, the only change is dropping the dev-mode
     branch in handleSubscribe().
     ============================================================ */
  function initRestockAlerts() {
    const modal = document.getElementById('rfd-restock-modal');
    if (!modal) return;

    const brandLabel = modal.querySelector('[data-restock-brand-label]');
    const brandLabel3 = modal.querySelector('[data-restock-brand-label3]');
    const subEl = modal.querySelector('[data-restock-sub]');
    const brandHandle = modal.querySelector('[data-restock-brand-handle]');
    const brandName = modal.querySelector('[data-restock-brand-name]');
    const productHandleEl = modal.querySelector('[data-restock-product-handle]');
    const productTitleEl = modal.querySelector('[data-restock-product-title]');
    const productLabel = modal.querySelector('[data-restock-product-label]');
    const scopeWrap = modal.querySelector('[data-restock-scope-wrap]');
    const modesWrap = modal.querySelector('[data-restock-modes-wrap]');
    const divider = modal.querySelector('[data-restock-divider]');
    const emailInput = modal.querySelector('#rfd-restock-email');
    const submitBtn = modal.querySelector('[data-restock-submit]');
    const resultEl = modal.querySelector('[data-restock-result]');

    // Product scope is always one-time → hide the once/every-time modes for it.
    // The divider only shows when the scope picker is present (product context)
    // AND brand is chosen, to separate the scope choice from the frequency modes.
    function setScopeUI(scope) {
      const showModes = scope !== 'product';
      if (modesWrap) modesWrap.style.display = showModes ? '' : 'none';
      if (divider) {
        const scopeVisible = scopeWrap && scopeWrap.style.display !== 'none';
        divider.style.display = showModes && scopeVisible ? '' : 'none';
      }
    }
    modal.querySelectorAll('input[name="rfd-restock-scope"]').forEach((r) => {
      r.addEventListener('change', () => setScopeUI(r.value));
    });

    function openModal(handle, name, prodHandle, prodTitle) {
      brandHandle.value = handle || '';
      brandName.value = name || '';
      if (productHandleEl) productHandleEl.value = prodHandle || '';
      if (productTitleEl) productTitleEl.value = prodTitle || '';
      if (brandLabel) brandLabel.textContent = name || 'this brand';
      if (brandLabel3) brandLabel3.textContent = name || 'this brand';

      const hasProduct = !!prodHandle;
      if (scopeWrap) scopeWrap.style.display = hasProduct ? '' : 'none';
      if (hasProduct) {
        if (productLabel) productLabel.textContent = prodTitle || 'this product';
        const pr = modal.querySelector('input[name="rfd-restock-scope"][value="product"]');
        if (pr) pr.checked = true;
        setScopeUI('product');
        if (subEl) subEl.textContent = "We'll email you once when " + (prodTitle || 'this product') + " is back in stock.";
      } else {
        setScopeUI('brand');
        if (subEl) subEl.textContent = "We'll email you when " + (name || 'this brand') + " restocks next.";
      }

      resultEl.style.display = 'none';
      resultEl.className = 'rfd-restock__result';
      resultEl.textContent = '';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Subscribe';
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-restock-open]');
      if (trigger) {
        e.preventDefault();
        openModal(trigger.dataset.brandHandle, trigger.dataset.brandName, trigger.dataset.productHandle, trigger.dataset.productTitle);
        return;
      }
      if (e.target.closest('[data-restock-close]')) closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    submitBtn.addEventListener('click', async () => {
      const email = (emailInput.value || '').trim();
      if (!email) {
        resultEl.style.display = 'block';
        resultEl.className = 'rfd-restock__result rfd-restock__result--error';
        resultEl.textContent = 'Please enter your email.';
        return;
      }
      const hasProduct = !!(productHandleEl && productHandleEl.value);
      let scope = 'brand';
      if (hasProduct) {
        scope = (modal.querySelector('input[name="rfd-restock-scope"]:checked') || {}).value || 'product';
      }
      const isProduct = scope === 'product';
      const mode = isProduct
        ? 'one_time'
        : (modal.querySelector('input[name="rfd-restock-mode"]:checked') || {}).value || 'one_time';
      const payload = {
        brand_handle: brandHandle.value,
        brand_name: brandName.value,
        email,
        mode,
        scope,
        product_handle: isProduct ? productHandleEl.value : '',
        product_title: isProduct && productTitleEl ? productTitleEl.value : '',
        customer_id: modal.dataset.customerId || null
      };

      submitBtn.disabled = true;
      submitBtn.textContent = 'Subscribing…';

      try {
        const r = await fetch('/apps/rfd-tools/restock/subscribe', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (r.ok) {
          resultEl.style.display = 'block';
          resultEl.className = 'rfd-restock__result rfd-restock__result--success';
          resultEl.innerHTML = isProduct
            ? `You're subscribed. We'll email <strong>${email}</strong> once when <strong>${payload.product_title || 'this product'}</strong> is back in stock.`
            : `You're subscribed. We'll email <strong>${email}</strong> when <strong>${payload.brand_name}</strong> restocks.`;
          submitBtn.textContent = 'Subscribed';
          submitBtn.disabled = true;
          setTimeout(closeModal, 1800);
          return;
        }
        throw new Error('app not ready');
      } catch (_) {
        // App not yet deployed — graceful stub
        resultEl.style.display = 'block';
        resultEl.className = 'rfd-restock__result rfd-restock__result--info';
        resultEl.innerHTML = `<strong>Restock alerts module is launching soon.</strong><br>Once live, you'll get notifications for <strong>${payload.brand_name}</strong>. Thanks for your patience.`;
        submitBtn.textContent = 'Got it';
        submitBtn.disabled = false;
      }
    });
  }

  function initStorefrontPopups() {
    var modal = document.getElementById('rfd-popup-modal');
    if (!modal) return; // logged-out: no container rendered
    function closeP(){ modal.classList.remove('is-open'); modal.setAttribute('aria-hidden','true'); document.body.style.overflow=''; }
    function openP(){ modal.classList.add('is-open'); modal.setAttribute('aria-hidden','false'); }
    modal.addEventListener('click', function(e){ if (e.target.closest('[data-popup-close]')) closeP(); });
    document.addEventListener('keydown', function(e){ if (e.key === 'Escape' && modal.classList.contains('is-open')) closeP(); });
    var popupUrl = '/apps/rfd-tools/popups/active' + (modal.dataset.staff === 'true' ? '?staff=1' : '');
    fetch(popupUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function(r){ return r.ok ? r.json() : null; })
      .then(function(data){
        var p = data && data.popup; if (!p) return;
        var imgWrap = modal.querySelector('[data-popup-image]');
        var img = modal.querySelector('[data-popup-img]');
        if (p.imageUrl) { img.src = p.imageUrl; imgWrap.style.display = ''; } else { imgWrap.style.display = 'none'; }
        imgWrap.style.background = p.imageBgImageUrl ? ('center/cover no-repeat url(' + p.imageBgImageUrl + ')') : (p.imageBgColor || '#000');
        imgWrap.classList.toggle('rfd-popup__image--cover', p.imageFit === 'cover');
        var cat = modal.querySelector('[data-popup-cat]');
        cat.textContent = p.categoryText || ''; cat.style.background = p.categoryBg; cat.style.color = p.categoryTextColor;
        modal.querySelector('[data-popup-heading]').textContent = p.heading || '';
        modal.querySelector('[data-popup-desc]').innerHTML = p.description || '';
        var cta = modal.querySelector('[data-popup-cta]');
        cta.innerHTML = '<span class="rfd-popup__cta-label"></span><svg class="rfd-popup__cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
        cta.querySelector('.rfd-popup__cta-label').textContent = p.ctaText || ''; cta.href = p.ctaUrl || '#';
        cta.style.background = p.ctaBg; cta.style.color = p.ctaTextColor;
        cta.style.display = p.ctaUrl ? '' : 'none';
        setTimeout(openP, 700);
      })
      .catch(function(){});
  }

  /* ============================================================
     COLLECTION — floating "scroll to top" button
     Only on collection pages (long product lists). Appears once
     the page is scrolled past a threshold; smooth-scrolls to top.
     ============================================================ */
  function initScrollTop() {
    if (!document.getElementById('rfd-collection-page')) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rfd-scrolltop';
    btn.setAttribute('aria-label', 'Scroll to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15"/></svg>';
    document.body.appendChild(btn);

    // Instant, not smooth: the theme sets `html { scroll-behavior: smooth }`,
    // and on a long collection (thousands of card nodes) a smooth scroll to top
    // crawls for several seconds. `behavior: 'instant'` overrides the CSS and
    // snaps straight up — the expected "back to top" feel.
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'instant' }));

    const THRESHOLD = 600;
    let ticking = false;
    function update() {
      ticking = false;
      btn.classList.toggle('is-visible', window.scrollY > THRESHOLD);
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  function init() {
    initSplash();
    initToasts();
    initNavHighlight();
    initScrollAnchors();
    initSteppers();
    initCartForms();
    initMarketingAutoCart();
    initCollection();
    initScrollTop();
    initProductCarousel();
    initProductTabs();
    initSampleForm();
    initSuggestForm();
    initContactForm();
    initApplyPage();
    initResourcesSearch();
    initCopyLinks();
    initOrderImport();
    initAccountV2();
    initRestockAlerts();
    initHeroVariant();
    initPreorderHome();
    initStorefrontPopups();
  }

  /* ============================================================
     HOME — Active Pre-Orders block (dynamic, App Proxy)
     ============================================================ */
  function initPreorderHome() {
    const root = document.querySelector('[data-preorder-home]');
    if (!root) return;
    const listEl = root.querySelector('[data-preorder-home-list]');
    if (!listEl) return;

    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    /* Campaign descriptions can carry markup from the admin editor — flatten to
       one line of plain text before escaping (CSS truncates it). */
    const stripTags = (s) => String(s == null ? '' : s).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const fmtDate = (d) => {
      if (!d) return '';
      const date = new Date(d);
      return isNaN(date) ? '' : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    fetch('/apps/rfd-tools/preorder/campaigns', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('failed'))))
      .then(data => {
        const campaigns = (data && data.campaigns) || [];
        if (!campaigns.length) { root.style.display = 'none'; return; }
        // Always at least 3 columns: with 1–2 campaigns the cards keep their
        // third-width and the remaining slots stay empty, so the row never
        // looks stretched. Beyond 3 the grid grows to match, capped at 5.
        const cols = Math.min(Math.max(campaigns.length, 3), 5);
        listEl.className = 'rfd-preorder-home__grid';
        listEl.style.setProperty('--po-cols', String(cols));
        listEl.innerHTML = campaigns.map(c => {
          const count = (c.items || []).length;
          const url = '/account?preorder=' + encodeURIComponent(c.id) + '#preorders';
          // Campaign artwork fills the band; a brand-logo fallback is contained
          // so logos aren't cropped (mirrors the pop-up's imageFit rule).
          const img = c.imageUrl
            ? `<img src="${esc(c.imageUrl)}" alt="" class="rfd-pohome-card__img${c.imageIsLogo ? ' is-logo' : ''}" loading="lazy">`
            : '<span class="rfd-pohome-card__noimg" aria-hidden="true"></span>';
          return `<a class="rfd-pohome-card" href="${esc(url)}">
              <span class="rfd-pohome-card__media">${img}</span>
              <span class="rfd-pohome-card__body">
                <span class="rfd-pohome-card__brand">${esc(c.brandName)}</span>
                <span class="rfd-pohome-card__name">${esc(c.name)}</span>
                ${c.description ? `<span class="rfd-pohome-card__desc">${esc(stripTags(c.description))}</span>` : ''}
                <span class="rfd-pohome-card__meta">
                  <span class="rfd-preorder-home__count">${count} product${count === 1 ? '' : 's'}</span>
                  <span class="rfd-preorder-home__date">Closes ${esc(fmtDate(c.cutoffAt))}</span>
                  ${c.etaAt ? `<span class="rfd-preorder-home__eta">ETA ${esc(fmtDate(c.etaAt))}</span>` : ''}
                </span>
                <span class="rfd-pohome-card__cta">Reserve
                  <svg class="rfd-preorder-home__arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </span>
              </span>
            </a>`;
        }).join('');
        root.style.display = '';
      })
      .catch(() => { root.style.display = 'none'; });
  }


  /* ───────────────── Pre-order top-up ─────────────────
     A dealer whose pre-order arrived has an UNPAID order holding their stock.
     If it's under the minimum they can't pay it at all until they add more, so
     the cart offers "add to that order" beside the normal checkout — reached
     from the email link or by simply shopping, since this is driven by their
     account state and not by a token in a URL. Adding goes onto the existing
     order: one order number, one invoice. Normal checkout is always still there
     for anyone who genuinely wants a separate order. */
  var rfdTopup = null;

  function rfdTopupMoney(cents, currency) {
    var n = (Number(cents) || 0) / 100;
    try {
      return n.toLocaleString(undefined, { style: 'currency', currency: currency || 'CAD' });
    } catch (_) {
      return '$' + n.toFixed(2);
    }
  }

  function rfdTopupPaint() {
    var t = rfdTopup;
    document.querySelectorAll('[data-topup-add]').forEach(function (btn) {
      if (!t) { btn.hidden = true; return; }
      btn.hidden = false;
      btn.textContent = 'Add to pre-order ' + t.orderName;
    });
    document.querySelectorAll('[data-topup-note]').forEach(function (el) {
      if (!t) { el.hidden = true; return; }
      el.hidden = false;
      el.textContent = t.shortfallCents > 0
        ? t.orderName + ' is held for you — ' + rfdTopupMoney(t.shortfallCents, t.currency) + ' short of the ' + rfdTopupMoney(t.minimumCents, t.currency) + ' minimum. Add these to it and you get one invoice.'
        : t.orderName + ' is awaiting payment. Add these to it and pay once, or check out separately.';
    });
    if (t && !document.getElementById('rfd-topup-bar') && !sessionStorage.getItem('rfd-topup-hide')) {
      var bar = document.createElement('div');
      bar.id = 'rfd-topup-bar';
      bar.className = 'rfd-topup-bar';
      bar.innerHTML =
        '<span class="rfd-topup-bar__text">' +
        (t.shortfallCents > 0
          ? '<strong>' + t.orderName + '</strong> is ready but ' + rfdTopupMoney(t.shortfallCents, t.currency) + ' under the ' + rfdTopupMoney(t.minimumCents, t.currency) + ' minimum — add anything to it and we invoice the lot.'
          : '<strong>' + t.orderName + '</strong> is awaiting payment — you can still add to it.') +
        '</span>' +
        '<a class="rfd-topup-bar__link" href="/collections/all">Browse products</a>' +
        '<button type="button" class="rfd-topup-bar__x" data-topup-dismiss aria-label="Hide">&times;</button>';
      document.body.appendChild(bar);
    }
  }

  function rfdLoadTopup() {
    if (!window.RFD_CUSTOMER_ID) return;
    fetch('/apps/rfd-tools/preorder/topup', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        rfdTopup = (d && d.topup) || null;
        rfdTopupPaint();
      })
      .catch(function () { /* storefront carries on unchanged */ });
  }

  async function rfdTopupSubmit(btn) {
    if (!rfdTopup || btn.dataset.busy) return;
    btn.dataset.busy = '1';
    var label = btn.textContent;
    btn.textContent = 'Adding…';
    btn.classList.add('is-loading');
    try {
      var cart = await rfdFetchCart();
      var items = ((cart && cart.items) || []).map(function (i) {
        return { variantId: String(i.variant_id), quantity: i.quantity };
      });
      if (!items.length) {
        window.rfdToast('Your cart is empty.');
        return;
      }
      var r = await fetch('/apps/rfd-tools/preorder/topup', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      var res = await r.json();
      if (!res || !res.ok) {
        window.rfdToast((res && res.error) || 'Could not add those items to your pre-order.');
        return;
      }
      await rfdClearCart().catch(function () {});
      var cur = rfdTopup.currency;
      if (res.cleared) {
        window.rfdToast('Added to ' + res.orderName + ' — ' + rfdTopupMoney(res.outstandingCents, cur) + ' now due. Invoice sent.');
        if (res.payUrl) window.location.href = res.payUrl;
      } else {
        window.rfdToast('Added to ' + res.orderName + ' — still ' + rfdTopupMoney(res.shortfallCents, cur) + ' under the minimum.');
      }
      rfdLoadTopup();
    } catch (_) {
      window.rfdToast('Could not add those items to your pre-order.');
    } finally {
      btn.dataset.busy = '';
      btn.textContent = label;
      btn.classList.remove('is-loading');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
